// ShadeReport.js

import { POLYGONS } from "../ThreeJs/Global/variables";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import generateReportIcon from "../assets/ui_revamp/GenerateReport.svg";
import sflLogo from "../assets/ui_revamp/SFL 3.png";
import shadeReportCover from "../assets/ui_revamp/shade-report-cover.png";
import irradianceBand from "../assets/ui_revamp/irradiance-band.png";
import redditSansRegular from "../assets/fonts/RedditSans/RedditSans-VariableFont_wght.ttf";
import redditSansItalic from "../assets/fonts/RedditSans/RedditSans-Italic-VariableFont_wght.ttf";
import { uploadDesignerReport } from "./reportUpload";
import { toast } from "../lib/toast";
import { isPolygonInsidePolygon } from "../ThreeJs/Files/Calculations/DormerIdentification";
import {
  fitImageContain,
  getPreferredOrganizationLogoUrl,
  loadImageAsDataUrl,
  loadImageDimensions,
  trimImageWhitespace,
} from "./reportBranding";

export async function generateShadeReport(currentDesign, reportPayload) {
  // ---------- Robust getters for dynamic design shapes ----------
  const pickFirstNonEmpty = (...vals) => {
    for (const v of vals) {
      if (typeof v === "string" && v.trim()) return v.trim();
      if (typeof v === "number" && Number.isFinite(v)) return String(v);
    }
    return "";
  };

  const nameFrom = (obj) => {
    if (!obj) return "";
    if (typeof obj === "string") return obj.trim();
    return pickFirstNonEmpty(
      obj.name,
      obj.fullName,
      obj.full_name,
      obj.companyName,
      obj.company_name,
      obj.title
    );
  };

  const getCustomerName = (design) =>
    pickFirstNonEmpty(
      nameFrom(design?.customer),
      design?.customerName,
      design?.customer_name,
      design?.clientName,
      design?.client_name,
      nameFrom(design?.client)
    ) || "N/A";

  const getDesignerName = (design) =>
    pickFirstNonEmpty(
      nameFrom(design?.designer),
      design?.designerName,
      design?.designer_name,
      nameFrom(design?.createdBy),
      design?.created_by_name,
      design?.createdByName
    ) || "N/A";

  const getOrganizationName = (design) =>
    pickFirstNonEmpty(
      nameFrom(design?.organization),
      design?.organizationName,
      design?.organization_name,
      nameFrom(design?.org),
      design?.orgName,
      design?.org_name
    ) || "N/A";

  // ---------- date format ----------
  const now = new Date();
  const formattedDateTime = (() => {
    const datePart = new Intl.DateTimeFormat("en-US", {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "2-digit",
    }).format(now);

    const timePart = new Intl.DateTimeFormat("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    }).format(now);

    return `${datePart} - ${timePart}`;
  })();

  // ---------- HELPERS FOR PERCENTAGE FORMATTING ----------
  function formatTwoSigFigs(value) {
    if (!isFinite(value) || value === 0) return "0";
    const abs = Math.abs(value);
    const digits = Math.floor(Math.log10(abs)) + 1;
    const decimals = Math.max(0, 2 - digits);
    return value.toFixed(decimals);
  }

  function formatPercentTwoSigFigs(ratio) {
    const percent = ratio * 100;
    return `${formatTwoSigFigs(percent)}%`;
  }

  // ---------- SUMMARY DATA FROM POLYGONS ----------
  const getSegmentLabel = (polygon, index) => {
    const labelFromElement =
      polygon?.label?.element?.innerText ||
      polygon?.label?.element?.textContent ||
      polygon?.label?.textContent;
    if (typeof labelFromElement === "string" && labelFromElement.trim()) {
      return labelFromElement.trim();
    }
    if (typeof polygon?.label === "string" && polygon.label.trim()) {
      return polygon.label.trim();
    }
    return String(index + 1);
  };

  const normalizeMonthlyASA = (monthlyASA) => {
    if (!Array.isArray(monthlyASA)) return Array.from({ length: 12 }, () => 0);
    return Array.from({ length: 12 }, (_, i) => {
      const entry =
        monthlyASA[i] || monthlyASA.find((m) => Number(m?.month) === i + 1);
      const value = entry?.asa || 0;
      return Math.round(value * 100);
    });
  };

  const roundCoord = (value) => {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? Number(numeric.toFixed(4)) : 0;
  };

  const isSamePoint = (a, b) => {
    if (!a || !b) return false;
    return (
      Math.abs((a.x ?? 0) - (b.x ?? 0)) < 1e-6 &&
      Math.abs((a.y ?? 0) - (b.y ?? 0)) < 1e-6 &&
      Math.abs((a.z ?? 0) - (b.z ?? 0)) < 1e-6
    );
  };

  const getSegmentBoundaryPoints = (polygon) => {
    const fromEdges = Array.isArray(polygon?.edges)
      ? polygon.edges
          .map((edge) => edge?.point1)
          .filter((p) => p && Number.isFinite(p.x) && Number.isFinite(p.y) && Number.isFinite(p.z))
          .map((p) => ({ x: roundCoord(p.x), y: roundCoord(p.y), z: roundCoord(p.z) }))
      : [];

    let points = fromEdges;

    if (!points.length) {
      const positionArray = polygon?.shape?.geometry?.attributes?.position?.array;
      if (positionArray && typeof positionArray.length === "number") {
        const fromGeometry = [];
        for (let i = 0; i < positionArray.length; i += 3) {
          const point = {
            x: roundCoord(positionArray[i]),
            y: roundCoord(positionArray[i + 1]),
            z: roundCoord(positionArray[i + 2]),
          };
          if (!fromGeometry.some((existing) => isSamePoint(existing, point))) {
            fromGeometry.push(point);
          }
        }
        points = fromGeometry;
      }
    }

    if (points.length > 1 && isSamePoint(points[0], points[points.length - 1])) {
      points = points.slice(0, -1);
    }

    return points;
  };

  const formatBoundarySummary = (boundaryPoints) => {
    if (!Array.isArray(boundaryPoints) || !boundaryPoints.length) return "N/A";
    return boundaryPoints
      .map((point, idx) => `P${idx + 1}(${point.x}, ${point.y}, ${point.z})`)
      .join("  ");
  };

  const polygonCandidates = (Array.isArray(POLYGONS) ? POLYGONS : []).map((polygon, index) => {
    const boundaryPoints = getSegmentBoundaryPoints(polygon);
    const boundaryKey =
      boundaryPoints.length > 0
        ? boundaryPoints.map((point) => `${point.x},${point.y},${point.z}`).join("|")
        : "";
    const fallbackLabel = getSegmentLabel(polygon, index);
    const dedupeKey =
      boundaryKey
        ? `boundary:${boundaryKey}`
        : polygon?.shape?.uuid
        ? `shape:${polygon.shape.uuid}`
        : polygon?.id
        ? `id:${polygon.id}`
        : `label:${fallbackLabel}`;

    return { polygon, boundaryPoints, dedupeKey };
  });

  const seenPolygonKeys = new Set();
  const uniquePolygonCandidates = [];
  polygonCandidates.forEach((entry) => {
    if (seenPolygonKeys.has(entry.dedupeKey)) return;
    seenPolygonKeys.add(entry.dedupeKey);
    uniquePolygonCandidates.push(entry);
  });

  if (polygonCandidates.length !== uniquePolygonCandidates.length) {
    console.warn("ShadeReport: duplicate segment entries detected; using unique segments only.", {
      totalPolygons: polygonCandidates.length,
      uniquePolygons: uniquePolygonCandidates.length,
    });
  }

  const segments = uniquePolygonCandidates.map(({ polygon, boundaryPoints }, index) => {
      const panels = polygon?.panels?.length || 0;
      const asa = polygon?.irradiance?.ASA ?? polygon?.irradiance?.asa ?? 0;
      const tof = polygon?.irradiance?.TOF ?? polygon?.irradiance?.tof ?? 0;
      const tsrf = asa * tof;
      const monthlyASA = normalizeMonthlyASA(polygon?.irradiance?.monthlyASA || []);
      const label = getSegmentLabel(polygon, index);

      return {
        id: index + 1,
        segmentGlobalId: polygon?.id ?? null,
        label,
        name: `Section ${label}`,
        panels,
        azimuth: polygon.details?.azimu ?? "-",
        pitch: polygon.details?.pitch ?? "-",
        tofDisplay: formatPercentTwoSigFigs(tof),
        solarAccessDisplay: formatPercentTwoSigFigs(asa),
        tsrfDisplay: formatPercentTwoSigFigs(tsrf),
        asa,
        tof,
        tsrf,
        monthlyASA,
        boundaryPoints,
        boundarySummary: formatBoundarySummary(boundaryPoints),
      };
    });

  const summary = segments;

  const totalPanels = summary.reduce((sum, r) => sum + r.panels, 0);

  const weightedASA =
    totalPanels > 0
      ? summary.reduce((sum, r) => sum + r.asa * r.panels, 0) / totalPanels
      : 0;

  const weightedTOF =
    totalPanels > 0
      ? summary.reduce((sum, r) => sum + r.tof * r.panels, 0) / totalPanels
      : 0;

  const weightedTSRF =
    totalPanels > 0
      ? summary.reduce((sum, r) => sum + r.tsrf * r.panels, 0) / totalPanels
      : 0;

  const monthlySolarAccess = segments.map((s) => s.monthlyASA);
  const overallMonthlySolarAccess = Array.from({ length: 12 }, (_, i) => {
    if (totalPanels === 0) return 0;
    const weighted = summary.reduce(
      (sum, s) => sum + (Number(s.monthlyASA?.[i]) || 0) * s.panels,
      0
    );
    return Math.round(weighted / totalPanels);
  });
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const toSegmentCode = (index) => {
    let n = Number(index);
    if (!Number.isFinite(n) || n < 0) return "";
    let label = "";
    do {
      label = String.fromCharCode(65 + (n % 26)) + label;
      n = Math.floor(n / 26) - 1;
    } while (n >= 0);
    return label;
  };

  // ---------- DESIGN META ----------
  const customerName = getCustomerName(currentDesign);
  const designerName = getDesignerName(currentDesign);
  const organizationName = getOrganizationName(currentDesign);

  const data = {
    title: `Shade Report - ${currentDesign?.name || "Untitled Design"}`,
    address: currentDesign?.address || "Address not available",
    coordinates:
      currentDesign?.latitude != null && currentDesign?.longitude != null
        ? `${currentDesign.latitude}, ${currentDesign.longitude}`
        : "N/A",
    dateTime: formattedDateTime,
    summary,
    weighted: {
      asa: `${(weightedASA * 100).toFixed(1)}%`,
      tof: `${(weightedTOF * 100).toFixed(1)}%`,
      tsrf: `${(weightedTSRF * 100).toFixed(1)}%`,
    },
    overall: {
      totalPanels,
      monthlySolarAccess: overallMonthlySolarAccess,
    },
    monthlySolarAccess,
    segments,
    initialSnapshot: reportPayload?.snapshot || "",
    segmentBaseSnapshot: reportPayload?.segmentBaseSnapshot || reportPayload?.snapshot || "",
    segmentIrradianceSnapshot:
      reportPayload?.segmentIrradianceSnapshot ||
      reportPayload?.segmentBaseSnapshot ||
      reportPayload?.snapshot ||
      "",
    mapTileImage: reportPayload?.mapTileImage || "",
  };

  const resolvePrimarySnapshot = (...candidates) =>
    candidates.find((candidate) => typeof candidate === "string" && candidate.startsWith("data:image/")) || "";

  // ---------- PDF GENERATOR ----------
  const generateShadePdf = async ({
    snapshotDataUrl,
    segmentSnapshots = {},
    shouldDownload = true,
  }) => {
    const doc = new jsPDF("p", "pt", "letter");
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    const leftMargin = 36;
    const rightMargin = 36;

    const shadeInk = [4, 34, 62];
    const shadeMuted = [163, 163, 163];
    const shadeBlack = [0, 0, 0];
    const shadeYellow = [229, 224, 70];
    const shadePageNumber = [54, 78, 101];
    const shadeBlack60 = [102, 102, 102];
    const shadeCardFill = [248, 250, 252];
    const shadeCardBorder = [204, 204, 204];
    const shadeTableAlt = [242, 244, 245];

    const headerLineY = 74;
    const footerLineY = pageHeight - 62;
    const headerFooterLineWidth = 0.8;
    const reportContentStartY = headerLineY + 22;
    const tableBottomMargin = pageHeight - footerLineY + 8;

    const loadAssetAsBase64 = async (assetUrl) => {
      const dataUrl = await loadImageAsDataUrl(assetUrl);
      const commaIndex = dataUrl.indexOf(",");
      return commaIndex >= 0 ? dataUrl.slice(commaIndex + 1) : "";
    };

    const loadReportLogo = async () => {
      const organizationLogoUrl = getPreferredOrganizationLogoUrl(currentDesign);
      const rawOrganizationLogoDataUrl = await loadImageAsDataUrl(organizationLogoUrl);
      const organizationLogoDataUrl = rawOrganizationLogoDataUrl
        ? await trimImageWhitespace(rawOrganizationLogoDataUrl)
        : "";
      if (organizationLogoDataUrl) return organizationLogoDataUrl;
      return loadImageAsDataUrl(sflLogo);
    };

    const registerRedditSans = async () => {
      try {
        const regularFontBase64 = await loadAssetAsBase64(redditSansRegular);
        if (!regularFontBase64) return false;

        const regularFontName = "RedditSans-Regular.ttf";
        doc.addFileToVFS(regularFontName, regularFontBase64);
        doc.addFont(regularFontName, "RedditSans", "normal");
        doc.addFont(regularFontName, "RedditSans", "bold");
        return true;
      } catch (err) {
        return false;
      }
    };

    let hasRedditSans = false;
    let sflLogoDataUrl = "";
    let logoPlacement = null;
    let coverImageDataUrl = "";
    let irradianceBandDataUrl = "";

    const setPdfFont = (style = "normal") => {
      const normalizedStyle = style === "bold" ? "bold" : "normal";
      if (hasRedditSans) {
        doc.setFont("RedditSans", normalizedStyle);
      } else {
        doc.setFont("helvetica", normalizedStyle);
      }
    };

    const getImageTypeFromDataUrl = (dataUrl) => {
      if (typeof dataUrl !== "string") return "JPEG";
      if (dataUrl.startsWith("data:image/png")) return "PNG";
      return "JPEG";
    };

    const addPdfImage = (sourceDataUrl, x, y, width, height) => {
      const imageType = getImageTypeFromDataUrl(sourceDataUrl);
      if (imageType === "JPEG") {
        doc.addImage(sourceDataUrl, imageType, x, y, width, height, undefined, "NONE");
        return;
      }
      doc.addImage(sourceDataUrl, imageType, x, y, width, height);
    };

    const isDataImage = (value) =>
      typeof value === "string" && value.startsWith("data:image/");

    const getAddressLines = (addressText, width, maxLines = 2) => {
      const safeAddress =
        typeof addressText === "string" && addressText.trim()
          ? addressText.trim()
          : "Address not available";

      const lines = doc.splitTextToSize(safeAddress, width);
      if (lines.length <= maxLines) return lines;

      const trimmed = lines.slice(0, maxLines);
      let tail = String(trimmed[maxLines - 1] || "");
      while (tail.length > 0 && doc.getTextWidth(`${tail}...`) > width) {
        tail = tail.slice(0, -1);
      }
      trimmed[maxLines - 1] = `${tail}...`;
      return trimmed;
    };

    const roundedImageCache = new Map();

    const drawRoundedCard = (x, y, width, height, radius, fillColor, borderColor) => {
      doc.setFillColor(...fillColor);
      doc.setDrawColor(...borderColor);
      doc.setLineWidth(0.6);
      doc.roundedRect(x, y, width, height, radius, radius, "FD");
    };

    const drawImageWithFallback = (sourceDataUrl, x, y, width, height) => {
      if (!isDataImage(sourceDataUrl)) {
        doc.setFillColor(238, 242, 245);
        doc.rect(x, y, width, height, "F");
        setPdfFont("normal");
        doc.setFontSize(10);
        doc.setTextColor(...shadeBlack60);
        doc.text("Snapshot not available", x + width / 2, y + height / 2, { align: "center" });
        return;
      }

      try {
        addPdfImage(sourceDataUrl, x, y, width, height);
      } catch (err) {
        doc.setFillColor(238, 242, 245);
        doc.rect(x, y, width, height, "F");
        setPdfFont("normal");
        doc.setFontSize(10);
        doc.setTextColor(...shadeBlack60);
        doc.text("Snapshot could not be embedded.", x + width / 2, y + height / 2, { align: "center" });
      }
    };

    const getRoundedImageDataUrl = async (sourceDataUrl, width, height, radius) => {
      if (!isDataImage(sourceDataUrl)) return "";
      const cacheKey = `${sourceDataUrl}|${Math.round(width)}|${Math.round(height)}|${Math.round(radius * 10)}`;
      if (roundedImageCache.has(cacheKey)) return roundedImageCache.get(cacheKey);

      const roundedPromise = new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          try {
            const sourceWidth = Number(img.naturalWidth || img.width) || 1;
            const sourceHeight = Number(img.naturalHeight || img.height) || 1;
            const targetWidth = Math.max(1, Number(width) || 1);
            const targetHeight = Math.max(1, Number(height) || 1);
            const maxScaleFromSource = Math.max(
              1,
              Math.min(sourceWidth / targetWidth, sourceHeight / targetHeight)
            );
            const qualityScale = Math.min(4, maxScaleFromSource);

            const canvas = document.createElement("canvas");
            canvas.width = Math.max(1, Math.round(targetWidth * qualityScale));
            canvas.height = Math.max(1, Math.round(targetHeight * qualityScale));
            const ctx = canvas.getContext("2d");
            if (!ctx) {
              resolve(sourceDataUrl);
              return;
            }

            const r = Math.max(
              0,
              Math.min(radius * qualityScale, Math.min(canvas.width, canvas.height) / 2)
            );
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.imageSmoothingEnabled = true;
            if (typeof ctx.imageSmoothingQuality === "string") {
              ctx.imageSmoothingQuality = "high";
            }
            ctx.beginPath();
            ctx.moveTo(r, 0);
            ctx.lineTo(canvas.width - r, 0);
            ctx.quadraticCurveTo(canvas.width, 0, canvas.width, r);
            ctx.lineTo(canvas.width, canvas.height - r);
            ctx.quadraticCurveTo(canvas.width, canvas.height, canvas.width - r, canvas.height);
            ctx.lineTo(r, canvas.height);
            ctx.quadraticCurveTo(0, canvas.height, 0, canvas.height - r);
            ctx.lineTo(0, r);
            ctx.quadraticCurveTo(0, 0, r, 0);
            ctx.closePath();
            ctx.clip();
            const drawScale = Math.min(
              canvas.width / sourceWidth,
              canvas.height / sourceHeight
            );
            const drawWidth = sourceWidth * drawScale;
            const drawHeight = sourceHeight * drawScale;
            const drawX = (canvas.width - drawWidth) / 2;
            const drawY = (canvas.height - drawHeight) / 2;

            ctx.fillStyle = "#ffffff";
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);
            resolve(canvas.toDataURL("image/png"));
          } catch (err) {
            resolve(sourceDataUrl);
          }
        };
        img.onerror = () => resolve(sourceDataUrl);
        img.src = sourceDataUrl;
      });

      roundedImageCache.set(cacheKey, roundedPromise);
      return roundedPromise;
    };

    const drawRoundedImageWithFallback = async (
      sourceDataUrl,
      x,
      y,
      width,
      height,
      radius,
      frameFill = [255, 255, 255],
      frameBorder = [4, 34, 62]
    ) => {
      doc.setFillColor(...frameFill);
      doc.setDrawColor(...frameBorder);
      doc.setLineWidth(0.6);
      doc.roundedRect(x, y, width, height, radius, radius, "FD");

      if (!isDataImage(sourceDataUrl)) {
        doc.setFillColor(238, 242, 245);
        doc.roundedRect(x, y, width, height, radius, radius, "F");
        return;
      }

      const roundedDataUrl = await getRoundedImageDataUrl(sourceDataUrl, width, height, radius);
      try {
        addPdfImage(roundedDataUrl, x, y, width, height);
      } catch (err) {
        drawImageWithFallback(sourceDataUrl, x, y, width, height);
      }
    };

    const getFixedTableMargins = (tableX, tableWidth, extraBottom = 0) => ({
      left: tableX,
      right: pageWidth - tableX - tableWidth,
      top: reportContentStartY,
      bottom: tableBottomMargin + extraBottom,
    });

    const tableCornerRadius = 8;

    const drawTableOutline = (tableX, startY, tableWidth, tableStartPage, tableEndPage, tableEndY) => {
      const tableBottomY = pageHeight - tableBottomMargin;
      const drawOnPage = (pageNumber, drawFn) => {
        const current = doc.internal.getCurrentPageInfo().pageNumber;
        doc.setPage(pageNumber);
        drawFn();
        doc.setPage(current);
      };

      doc.setDrawColor(...shadeTableAlt);
      doc.setLineWidth(0.6);

      if (tableStartPage === tableEndPage) {
        drawOnPage(tableStartPage, () => {
          doc.roundedRect(
            tableX,
            startY,
            tableWidth,
            tableEndY - startY,
            tableCornerRadius,
            tableCornerRadius,
            "S"
          );
        });
        return;
      }

      drawOnPage(tableStartPage, () => {
        doc.roundedRect(
          tableX,
          startY,
          tableWidth,
          tableBottomY - startY,
          tableCornerRadius,
          tableCornerRadius,
          "S"
        );
      });

      if (tableEndPage - tableStartPage > 1) {
        for (let pageIndex = tableStartPage + 1; pageIndex < tableEndPage; pageIndex += 1) {
          drawOnPage(pageIndex, () => {
            doc.rect(tableX, reportContentStartY, tableWidth, tableBottomY - reportContentStartY, "S");
          });
        }
      }

      drawOnPage(tableEndPage, () => {
        doc.roundedRect(
          tableX,
          reportContentStartY,
          tableWidth,
          tableEndY - reportContentStartY,
          tableCornerRadius,
          tableCornerRadius,
          "S"
        );
      });
    };

    const normalizePdfColor = (colorValue, fallback = [255, 255, 255]) => {
      if (Array.isArray(colorValue) && colorValue.length >= 3) {
        return colorValue.slice(0, 3).map((n) => Number(n) || 0);
      }
      if (typeof colorValue === "number" && Number.isFinite(colorValue)) {
        const v = Math.max(0, Math.min(255, colorValue));
        return [v, v, v];
      }
      return fallback;
    };

    const carveTableCorner = (cell, corner, fillColor) => {
      if (!cell) return;
      const radius = Math.max(
        0,
        Math.min(tableCornerRadius, Number(cell.width) || 0, Number(cell.height) || 0)
      );
      if (radius <= 0) return;

      const x = Number(cell.x) || 0;
      const y = Number(cell.y) || 0;
      const w = Number(cell.width) || 0;
      const h = Number(cell.height) || 0;

      doc.setFillColor(255, 255, 255);
      if (corner === "top-left") {
        doc.rect(x, y, radius, radius, "F");
        doc.setFillColor(...fillColor);
        doc.circle(x + radius, y + radius, radius, "F");
        return;
      }
      if (corner === "top-right") {
        doc.rect(x + w - radius, y, radius, radius, "F");
        doc.setFillColor(...fillColor);
        doc.circle(x + w - radius, y + radius, radius, "F");
        return;
      }
      if (corner === "bottom-left") {
        doc.rect(x, y + h - radius, radius, radius, "F");
        doc.setFillColor(...fillColor);
        doc.circle(x + radius, y + h - radius, radius, "F");
        return;
      }
      if (corner === "bottom-right") {
        doc.rect(x + w - radius, y + h - radius, radius, radius, "F");
        doc.setFillColor(...fillColor);
        doc.circle(x + w - radius, y + h - radius, radius, "F");
      }
    };

    const drawRoundedTableCorners = (hookData) => {
      const section = hookData?.section;
      const row = hookData?.row;
      const column = hookData?.column;
      const cell = hookData?.cell;
      const table = hookData?.table;
      if (!row || !column || !cell || !table) return;

      const firstColumn = column.index === 0;
      const lastColumn = column.index === table.columns.length - 1;

      if (section === "head" && row.index === 0) {
        const fill = normalizePdfColor(cell.styles?.fillColor, shadeInk);
        if (firstColumn) carveTableCorner(cell, "top-left", fill);
        if (lastColumn) carveTableCorner(cell, "top-right", fill);
        return;
      }

      if (section === "body" && row.index === table.body.length - 1) {
        const fill = normalizePdfColor(
          cell.styles?.fillColor,
          normalizePdfColor(row.styles?.fillColor, [255, 255, 255])
        );
        if (firstColumn) carveTableCorner(cell, "bottom-left", fill);
        if (lastColumn) carveTableCorner(cell, "bottom-right", fill);
      }
    };

    const drawHeader = (projectAddress) => {
      const logoW = 111;
      const logoH = 20;
      const logoX = leftMargin;
      const logoY = 24;

      if (sflLogoDataUrl) {
        try {
          addPdfImage(
            sflLogoDataUrl,
            logoPlacement?.x ?? logoX,
            logoPlacement?.y ?? logoY,
            logoPlacement?.width ?? logoW,
            logoPlacement?.height ?? logoH
          );
        } catch (err) {}
      } else {
        setPdfFont("bold");
        doc.setFontSize(20);
        doc.setTextColor(...shadeInk);
        doc.text("SFL", logoX, logoY + 18);
      }

      setPdfFont("bold");
      doc.setFontSize(14);
      doc.setTextColor(...shadeInk);
      const addressLines = getAddressLines(projectAddress, 290, 2);
      doc.text(addressLines, pageWidth - rightMargin, 42, { align: "right" });

      doc.setDrawColor(...shadeInk);
      doc.setLineWidth(headerFooterLineWidth);
      doc.line(leftMargin, headerLineY, pageWidth - rightMargin, headerLineY);
    };

    const drawFooter = (pageIndex) => {
      doc.setDrawColor(...shadeInk);
      doc.setLineWidth(headerFooterLineWidth);
      doc.line(leftMargin, footerLineY, pageWidth - rightMargin, footerLineY);

      const labelY = footerLineY + 18;
      const valueY = labelY + 14;

      setPdfFont("normal");
      doc.setFontSize(10);
      doc.setTextColor(...shadeMuted);
      doc.text("Organization", leftMargin, labelY);
      doc.text("Designer Name", pageWidth - rightMargin, labelY, { align: "right" });

      setPdfFont("normal");
      doc.setFontSize(12);
      doc.setTextColor(...shadeInk);
      doc.text("Empower Designs", leftMargin, valueY);
      doc.text("Bruce Warren", pageWidth - rightMargin, valueY, { align: "right" });

      setPdfFont("normal");
      doc.setFontSize(12);
      doc.setTextColor(...shadePageNumber);
      doc.text(String(pageIndex), pageWidth / 2, valueY, { align: "center" });
    };

    hasRedditSans = await registerRedditSans();
    [sflLogoDataUrl, coverImageDataUrl, irradianceBandDataUrl] = await Promise.all([
      loadReportLogo(),
      loadImageAsDataUrl(shadeReportCover),
      loadImageAsDataUrl(irradianceBand),
    ]);
    logoPlacement = fitImageContain(
      await loadImageDimensions(sflLogoDataUrl || sflLogo),
      leftMargin,
      19,
      166.5,
      30,
      { alignX: "left" }
    );
    const tableFont = hasRedditSans ? "RedditSans" : "helvetica";

    // ---------- COVER PAGE ----------
    drawHeader(data.address);

    doc.setTextColor(...shadeBlack);
    setPdfFont("bold");
    doc.setFontSize(36);
    doc.text("Shade Analysis", leftMargin, 148);

    setPdfFont("normal");
    doc.setFontSize(36);
    doc.text("Report", leftMargin, 186);

    doc.setDrawColor(...shadeYellow);
    doc.setLineWidth(4);
    doc.line(leftMargin, 203, leftMargin + 60, 203);

    const coverW = 500;
    const coverH = 477;
    const coverX = (pageWidth - coverW) / 2;
    const coverY = 220;
    if (coverImageDataUrl) {
      try {
        addPdfImage(coverImageDataUrl, coverX, coverY, coverW, coverH);
      } catch (err) {}
    }

    // ---------- REPORT DATA (starts on page 2) ----------
    doc.addPage();

    const sectionWidth = 555;
    const sectionX = (pageWidth - sectionWidth) / 2;
    const fixedTableWidth = 520;
    const fixedTableX = (pageWidth - fixedTableWidth) / 2;
    const fixedTableMargins = getFixedTableMargins(fixedTableX, fixedTableWidth);
    const mainLegendCardWidth = 190;
    const mainLegendCardHeight = 200;
    const mainLegendSnapshotGap = 15;
    const mainSnapshotWidth = 333;
    const mainSnapshotHeight = 200;
    const segmentDetailsHeaderHeight = 60;
    const segmentCardWidth = 540;
    const segmentCardX = (pageWidth - segmentCardWidth) / 2;
    const segmentCardHeight = 197;
    const segmentCardGap = 20;
    const segmentMonthInitials = ["J", "F", "M", "A", "M", "J", "J", "A", "S", "O", "N", "D"];

    const summarySnapshot = resolvePrimarySnapshot(
      snapshotDataUrl,
      data.initialSnapshot,
      data.segmentBaseSnapshot,
      data.segmentIrradianceSnapshot
    );

    const getSegmentSnapshotPair = (segId) => {
      const payload = segmentSnapshots?.[String(segId)] || "";
      const highlightedRaw =
        typeof payload === "string"
          ? payload
          : payload?.highlightedSnapshot ||
            payload?.highlighted ||
            data.segmentIrradianceSnapshot ||
            data.initialSnapshot ||
            "";
      const croppedRaw =
        typeof payload === "string"
          ? data.segmentBaseSnapshot || data.initialSnapshot || payload
          : payload?.croppedSnapshot ||
            payload?.cropped ||
            data.segmentBaseSnapshot ||
            data.initialSnapshot ||
            highlightedRaw ||
            "";

      return {
        highlighted: isDataImage(highlightedRaw) ? highlightedRaw : "",
        cropped: isDataImage(croppedRaw) ? croppedRaw : "",
      };
    };

    const drawSegmentHeaderCard = (yStart) => {
      drawRoundedCard(
        sectionX,
        yStart,
        sectionWidth,
        segmentDetailsHeaderHeight,
        15,
        shadeCardFill,
        shadeCardBorder
      );

      setPdfFont("bold");
      doc.setFontSize(20);
      doc.setTextColor(...shadeBlack);
      doc.text("Sections Details", sectionX + 16, yStart + 38);

      setPdfFont("normal");
      doc.setFontSize(12);
      doc.setTextColor(...shadeBlack60);
      const labelX = sectionX + 268;
      doc.text("Irradiance", labelX, yStart + 26);
      doc.text("(kWh/m2/yr)", labelX, yStart + 41);

      if (isDataImage(irradianceBandDataUrl)) {
        const bandWidth = 147.27;
        const bandHeight = 18;
        const bandX = sectionX + sectionWidth - 16 - bandWidth;
        const bandY = yStart + (segmentDetailsHeaderHeight - bandHeight) / 2;
        try {
          addPdfImage(irradianceBandDataUrl, bandX, bandY, bandWidth, bandHeight);
        } catch (err) {}
      }

      return yStart + segmentDetailsHeaderHeight + 20;
    };

    const drawSegmentCard = async (seg, yStart) => {
      const cardX = segmentCardX;
      const titleY = yStart + 20;
      const metricX = cardX;
      const metricRowWidth = 200;
      const metricRowHeight = 12;
      const metricRowGap = 4;
      const metricsTopY = yStart + 34;
      const chartX = metricX;
      const chartY = yStart + 95;
      const chartWidth = 200;
      const chartHeight = 82;
      const chartPlotHeight = 66;
      const imageY = yStart + 30;
      const imageWidth = 150;
      const imageHeight = 160;
      const imageGap = 10;
      const imageRadius = 15;
      const image1X = cardX + 230;
      const image2X = image1X + imageWidth + imageGap;

      setPdfFont("normal");
      doc.setFontSize(20);
      doc.setTextColor(...shadeBlack);
      doc.text(`Section ${seg.label}`, cardX, titleY);

      const metricRows = [
        ["Annual TSRF (%)", String(seg.tsrfDisplay || "N/A")],
        ["Annual TOF (%)", String(seg.tofDisplay || "N/A")],
        ["Annual Solar Access (%)", String(seg.solarAccessDisplay || "N/A")],
      ];

      metricRows.forEach(([label, value], idx) => {
        const rowTop = metricsTopY + idx * (metricRowHeight + metricRowGap);
        const textY = rowTop + metricRowHeight - 2;

        setPdfFont("normal");
        doc.setFontSize(10);
        doc.setTextColor(...shadeMuted);
        doc.text(label, metricX, textY);

        setPdfFont("normal");
        doc.setFontSize(10);
        doc.setTextColor(...shadeBlack);
        doc.text(value, metricX + metricRowWidth, textY, { align: "right" });
      });

      const barGap = 4;
      const barWidth = (chartWidth - barGap * 11) / 12;
      const barMaxHeight = chartPlotHeight - 8;
      const chartBaseY = chartY + chartPlotHeight;

      for (let i = 0; i < 12; i += 1) {
        const monthValue = Number(seg.monthlyASA?.[i] ?? 0) || 0;
        const barHeight = Math.max(2, (monthValue / 100) * barMaxHeight);
        const barX = chartX + i * (barWidth + barGap);
        const barY = chartBaseY - barHeight;
        const barRadius = Math.max(0, Math.min(3, barWidth / 2, barHeight / 2));

        doc.setFillColor(...shadeInk);
        doc.roundedRect(barX, barY, barWidth, barHeight, barRadius, barRadius, "F");

        setPdfFont("normal");
        doc.setFontSize(10);
        doc.setTextColor(...shadeBlack);
        doc.text(segmentMonthInitials[i], barX + barWidth / 2, chartY + chartHeight - 2, {
          align: "center",
        });
      }

      const snapshotPair = getSegmentSnapshotPair(seg.id);
      await drawRoundedImageWithFallback(
        snapshotPair.highlighted,
        image1X,
        imageY,
        imageWidth,
        imageHeight,
        imageRadius,
        [255, 255, 255],
        [255, 255, 255]
      );
      await drawRoundedImageWithFallback(
        snapshotPair.cropped,
        image2X,
        imageY,
        imageWidth,
        imageHeight,
        imageRadius,
        [255, 255, 255],
        [255, 255, 255]
      );
    };

    let y = reportContentStartY;
    setPdfFont("bold");
    doc.setFontSize(20);
    doc.setTextColor(...shadeBlack);
    doc.text("Annual Summary", sectionX, y + 18);
    y += 30;

    const summaryRowWidth = mainLegendCardWidth + mainLegendSnapshotGap + mainSnapshotWidth;
    const summaryRowX = sectionX + (sectionWidth - summaryRowWidth) / 2;

    drawRoundedCard(
      summaryRowX,
      y,
      mainLegendCardWidth,
      mainLegendCardHeight,
      15,
      shadeCardFill,
      shadeCardBorder
    );
    setPdfFont("normal");
    doc.setFontSize(12);
    doc.setTextColor(...shadeBlack60);
    const legendCenterX = summaryRowX + mainLegendCardWidth / 2;
    const legendTextY = y + mainLegendCardHeight / 2 - 8;
    doc.text("Irradiance (kWh/m2/yr)", legendCenterX, legendTextY, { align: "center" });
    if (isDataImage(irradianceBandDataUrl)) {
      const bandWidth = 152;
      const bandHeight = 18;
      const bandX = legendCenterX - bandWidth / 2;
      const bandY = legendTextY + 8;
      try {
        addPdfImage(irradianceBandDataUrl, bandX, bandY, bandWidth, bandHeight);
      } catch (err) {}
    }

    const summarySnapshotX = summaryRowX + mainLegendCardWidth + mainLegendSnapshotGap;
    drawRoundedCard(
      summarySnapshotX,
      y,
      mainSnapshotWidth,
      mainSnapshotHeight,
      15,
      [255, 255, 255],
      shadeCardBorder
    );
    await drawRoundedImageWithFallback(
      summarySnapshot,
      summarySnapshotX,
      y,
      mainSnapshotWidth,
      mainSnapshotHeight,
      15,
      [255, 255, 255],
      shadeCardBorder
    );
    y += mainLegendCardHeight + 24;

    setPdfFont("bold");
    doc.setFontSize(20);
    doc.setTextColor(...shadeBlack);
    doc.text("Section Summary", fixedTableX, y + 18);
    y += 26;

    const summaryRows = (data.summary || []).map((r, i) => [
      String(toSegmentCode(i)),
      String(r.azimuth),
      String(r.pitch),
      String(r.tofDisplay),
      String(r.solarAccessDisplay),
      String(r.tsrfDisplay),
    ]);

    const summaryTableStartY = y + 8;
    const summaryStartPage = doc.internal.getCurrentPageInfo().pageNumber;
    autoTable(doc, {
      startY: summaryTableStartY,
      margin: fixedTableMargins,
      tableWidth: fixedTableWidth,
      theme: "striped",
      head: [["Sections", "Azimuth", "Pitch", "TOF (%)", "Annual Solar Access (%)", "Annual TSRF (%)"]],
      body: summaryRows,
      styles: {
        font: tableFont,
        fontSize: 8,
        cellPadding: { top: 6, right: 8, bottom: 6, left: 8 },
        textColor: shadeBlack,
        lineWidth: 0,
        valign: "middle",
      },
      headStyles: {
        fillColor: shadeInk,
        textColor: [255, 255, 255],
        fontStyle: "bold",
        halign: "left",
        cellPadding: { top: 6, right: 10, bottom: 6, left: 12 },
        minCellHeight: 26,
        lineWidth: 0,
      },
      bodyStyles: {
        fillColor: [255, 255, 255],
        halign: "left",
        minCellHeight: 22,
        lineWidth: 0,
      },
      alternateRowStyles: { fillColor: shadeTableAlt },
      tableLineWidth: 0,
      didDrawCell: drawRoundedTableCorners,
    });
    const summaryEndPage = doc.internal.getCurrentPageInfo().pageNumber;
    drawTableOutline(
      fixedTableX,
      summaryTableStartY,
      fixedTableWidth,
      summaryStartPage,
      summaryEndPage,
      doc.lastAutoTable.finalY
    );

    y = doc.lastAutoTable.finalY + 20;
    setPdfFont("bold");
    doc.setFontSize(20);
    doc.setTextColor(...shadeBlack);
    doc.text("Monthly Solar Access % by Section", fixedTableX, y + 18);
    y += 26;

    const monthlyRows = (data.monthlySolarAccess || []).map((row, i) => [
      String(toSegmentCode(i)),
      ...(row || []).map((v) => String(v)),
    ]);
    const monthlyContinuationReserve = 22;
    const monthlyMargins = getFixedTableMargins(fixedTableX, fixedTableWidth, monthlyContinuationReserve);
    const monthlyStartY = y + 8;
    const monthlyStartPage = doc.internal.getCurrentPageInfo().pageNumber;

    autoTable(doc, {
      startY: monthlyStartY,
      margin: monthlyMargins,
      tableWidth: fixedTableWidth,
      theme: "striped",
      head: [["Sections", ...months]],
      body: monthlyRows,
      styles: {
        font: tableFont,
        fontSize: 7,
        cellPadding: { top: 5, right: 6, bottom: 5, left: 6 },
        textColor: shadeBlack,
        lineWidth: 0,
        valign: "middle",
      },
      headStyles: {
        fillColor: shadeInk,
        textColor: [255, 255, 255],
        fontStyle: "bold",
        halign: "left",
        cellPadding: { top: 5, right: 8, bottom: 5, left: 10 },
        minCellHeight: 24,
        lineWidth: 0,
      },
      bodyStyles: {
        fillColor: [255, 255, 255],
        halign: "left",
        minCellHeight: 18,
        lineWidth: 0,
      },
      alternateRowStyles: { fillColor: shadeTableAlt },
      tableLineWidth: 0,
      didDrawCell: drawRoundedTableCorners,
    });
    const monthlyEndPage = doc.internal.getCurrentPageInfo().pageNumber;
    drawTableOutline(
      fixedTableX,
      monthlyStartY,
      fixedTableWidth,
      monthlyStartPage,
      monthlyEndPage,
      doc.lastAutoTable.finalY
    );
    if (monthlyEndPage > monthlyStartPage) {
      doc.setPage(monthlyStartPage);
      setPdfFont("normal");
      doc.setFontSize(9);
      doc.setTextColor(...shadeBlack60);
      doc.text("Continued to next page ->", fixedTableX + fixedTableWidth, footerLineY - 14, {
        align: "right",
      });
      doc.setPage(monthlyEndPage);
    }

    if (Array.isArray(data.segments) && data.segments.length > 0) {
      doc.addPage();
      let detailsY = drawSegmentHeaderCard(reportContentStartY);
      for (let segIndex = 0; segIndex < data.segments.length; segIndex += 1) {
        const seg = data.segments[segIndex];
        if (detailsY + segmentCardHeight > footerLineY - 12) {
          doc.addPage();
          detailsY = drawSegmentHeaderCard(reportContentStartY);
        }
        await drawSegmentCard(seg, detailsY);
        if (segIndex < data.segments.length - 1) {
          const separatorY = detailsY + segmentCardHeight + segmentCardGap / 2;
          if (separatorY < footerLineY - 8) {
            doc.setDrawColor(204, 204, 204);
            doc.setLineWidth(0.6);
            doc.line(segmentCardX, separatorY, segmentCardX + segmentCardWidth, separatorY);
          }
        }
        detailsY += segmentCardHeight + segmentCardGap;
      }
    }

    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      drawHeader(data.address);
      drawFooter(i);
    }

    const fileName = `Shade_Report_${currentDesign?.name || "Untitled"}.pdf`;
    const pdfArrayBuffer = doc.output("arraybuffer");
    const pdfBlob = new Blob([pdfArrayBuffer], { type: "application/pdf" });

    const uploadToken = reportPayload?.auth?.token || "";
    const uploadProjectId = reportPayload?.project?.id || "";
    await uploadDesignerReport({
      token: uploadToken,
      projectId: uploadProjectId,
      reportType: "shade",
      fileBlob: pdfBlob,
      fileName,
    });

    if (shouldDownload !== false) {
      doc.save(fileName);
    }
  };

  const isDataImage = (value) =>
    typeof value === "string" && value.startsWith("data:image/");

  const loadDataImage = (src) =>
    new Promise((resolve) => {
      if (!isDataImage(src)) {
        resolve(null);
        return;
      }
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => resolve(null);
      img.src = src;
    });

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  // ---------- Use the already-open popup window ----------
  const page =
    reportPayload?.shouldDownload === false
      ? null
      : reportPayload?.__reportWindowRef || window.open("", "_blank");
  if (reportPayload?.shouldDownload !== false && !page) {
    toast.warning("Popup blocked. Please allow popups for this site to view the report.", {
      key: "shade-report-popup-blocked",
      duration: 7000,
    });
    return;
  }

  const notifyReportWindow = (message) => {
    try {
      if (page && !page.closed) {
        page.postMessage(message, "*");
      }
    } catch (err) {
      // Ignore popup messaging failures and let the PDF flow continue.
    }
  };

  // Listen for snapshot messages from popup (PDF trigger)
  let pdfGenerationInProgress = false;
  async function onSnapshotMsg(event) {
    if (event?.data?.type !== "SHADE_REPORT_SNAPSHOT") return;
    if (pdfGenerationInProgress) return;

    pdfGenerationInProgress = true;
    notifyReportWindow({ type: "SHADE_REPORT_PDF_STATUS", status: "loading" });

    const snapshotDataUrl = event?.data?.snapshot;
    const segmentSnapshots = event?.data?.segmentSnapshots || {};

    try {
      await generateShadePdf({ snapshotDataUrl, segmentSnapshots });
      notifyReportWindow({ type: "SHADE_REPORT_PDF_STATUS", status: "idle" });
    } catch (err) {
      console.error("generateShadePdf failed:", err);
      notifyReportWindow({
        type: "SHADE_REPORT_PDF_STATUS",
        status: "error",
        message: String(err?.message || err || "Failed to generate PDF."),
      });
    } finally {
      pdfGenerationInProgress = false;
    }
  }
  if (page) {
    window.addEventListener("message", onSnapshotMsg);
  }

  // Ready promise
  const readyPromise = page
    ? new Promise((resolve) => {
        function handleMessage(event) {
          if (event?.data?.type === "SHADE_REPORT_READY") {
            window.removeEventListener("message", handleMessage);
            resolve();
          }
        }
        window.addEventListener("message", handleMessage);
      })
    : Promise.resolve();

  // For each segment, find the segments that are dormers of it — i.e. whose
  // footprint lies fully inside it (same containment test the DXF pipeline uses).
  // These outlines are sent to the viewer so the dormer can be masked out of the
  // parent segment's crop/irradiance, keeping it visually separate.
  const normalizeBoundary = (boundaryPoints) =>
    Array.isArray(boundaryPoints)
      ? boundaryPoints.map((point) => ({
          x: Number(point?.x) || 0,
          y: Number(point?.y) || 0,
          z: Number(point?.z) || 0,
        }))
      : [];

  const getDormerBoundariesFor = (segIndex) => {
    const outer = (data.segments || [])[segIndex]?.boundaryPoints;
    if (!Array.isArray(outer) || outer.length < 3) return [];
    const dormers = [];
    (data.segments || []).forEach((other, otherIndex) => {
      if (otherIndex === segIndex) return;
      const inner = other?.boundaryPoints;
      if (!Array.isArray(inner) || inner.length < 3) return;
      if (isPolygonInsidePolygon(inner, outer)) {
        dormers.push(normalizeBoundary(inner));
      }
    });
    return dormers;
  };

  const segmentMetaForViewer = (data.segments || []).map((seg, index) => ({
    id: String(seg.id),
    label: seg.label,
    name: seg.name || `Section ${seg.label}`,
    boundaryPoints: normalizeBoundary(seg.boundaryPoints),
    dormerBoundaries: getDormerBoundariesFor(index),
    boundarySummary: seg.boundarySummary || "",
  }));

  const buildProjectionCamera = (width, height) => {
    const THREERef = typeof window !== "undefined" ? window.THREE : null;
    if (!THREERef || !width || !height) return null;

    const cameraDef = reportPayload?.segmentBaseCamera || reportPayload?.camera || {};
    const projectionCamera = new THREERef.PerspectiveCamera(
      cameraDef?.fov || 45,
      width / height,
      0.1,
      5000
    );

    const up = cameraDef?.up || [0, 0, 1];
    const position = cameraDef?.position || [0, 0, 50];
    const target = cameraDef?.target || [0, 0, 0];

    projectionCamera.up.set(up[0] || 0, up[1] || 0, up[2] || 1);
    projectionCamera.position.set(position[0] || 0, position[1] || 0, position[2] || 50);
    projectionCamera.lookAt(new THREERef.Vector3(target[0] || 0, target[1] || 0, target[2] || 0));
    projectionCamera.updateMatrixWorld(true);
    projectionCamera.updateProjectionMatrix();
    return projectionCamera;
  };

  const projectBoundaryPoints = (boundaryPoints, width, height) => {
    const THREERef = typeof window !== "undefined" ? window.THREE : null;
    const projectionCamera = buildProjectionCamera(width, height);
    if (!THREERef || !projectionCamera || !Array.isArray(boundaryPoints) || boundaryPoints.length < 3) {
      return [];
    }

    return boundaryPoints.map((point) => {
      const vec = new THREERef.Vector3(point.x || 0, point.y || 0, point.z || 0);
      vec.project(projectionCamera);
      return {
        x: ((vec.x + 1) * 0.5) * width,
        y: ((1 - vec.y) * 0.5) * height,
      };
    });
  };

  const tracePolygonPath = (ctx, points, shiftX = 0, shiftY = 0) => {
    if (!ctx || !Array.isArray(points) || points.length < 3) return;
    ctx.beginPath();
    points.forEach((point, index) => {
      const x = point.x - shiftX;
      const y = point.y - shiftY;
      if (index === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    ctx.closePath();
  };

  const buildSegmentImagePair = (
    baseImg,
    irradianceImg,
    boundaryPoints,
    dormerBoundaries = []
  ) => {
    const sourceWidth = baseImg?.naturalWidth || baseImg?.width || 1;
    const sourceHeight = baseImg?.naturalHeight || baseImg?.height || 1;
    const maxDimension = 1400;
    const downscale = Math.max(1, Math.max(sourceWidth, sourceHeight) / maxDimension);
    const width = Math.max(1, Math.round(sourceWidth / downscale));
    const height = Math.max(1, Math.round(sourceHeight / downscale));

    const projectedPoints = projectBoundaryPoints(boundaryPoints, width, height);
    if (projectedPoints.length < 3) {
      return {
        highlightedSnapshot: baseImg?.src || "",
        croppedSnapshot: baseImg?.src || "",
      };
    }

    const projectedDormers = (Array.isArray(dormerBoundaries) ? dormerBoundaries : [])
      .map((dormer) => projectBoundaryPoints(dormer, width, height))
      .filter((points) => Array.isArray(points) && points.length >= 3);

    const highlightCanvas = document.createElement("canvas");
    highlightCanvas.width = width;
    highlightCanvas.height = height;
    const highlightCtx = highlightCanvas.getContext("2d");
    if (!highlightCtx) {
      return {
        highlightedSnapshot: baseImg?.src || "",
        croppedSnapshot: baseImg?.src || "",
      };
    }

    highlightCtx.drawImage(baseImg, 0, 0, width, height);

    if (irradianceImg) {
      highlightCtx.save();
      tracePolygonPath(highlightCtx, projectedPoints);
      highlightCtx.clip();
      highlightCtx.drawImage(irradianceImg, 0, 0, width, height);
      highlightCtx.restore();
    }

    projectedDormers.forEach((dormerPoints) => {
      highlightCtx.save();
      tracePolygonPath(highlightCtx, dormerPoints);
      highlightCtx.clip();
      highlightCtx.drawImage(baseImg, 0, 0, width, height);
      highlightCtx.fillStyle = "rgba(255, 255, 255, 0.5)";
      highlightCtx.fillRect(0, 0, width, height);
      highlightCtx.restore();
    });

    const boundedPoints = projectedPoints.map((point) => ({
      x: clamp(point.x, 0, width),
      y: clamp(point.y, 0, height),
    }));

    let minX = Math.min(...boundedPoints.map((point) => point.x));
    let minY = Math.min(...boundedPoints.map((point) => point.y));
    let maxX = Math.max(...boundedPoints.map((point) => point.x));
    let maxY = Math.max(...boundedPoints.map((point) => point.y));

    if (![minX, minY, maxX, maxY].every(Number.isFinite)) {
      return {
        highlightedSnapshot: highlightCanvas.toDataURL("image/jpeg", 0.92),
        croppedSnapshot: baseImg?.src || "",
      };
    }

    const pad = Math.max(18, Math.round(Math.min(width, height) * 0.08));
    minX = clamp(Math.floor(minX - pad), 0, width - 1);
    minY = clamp(Math.floor(minY - pad), 0, height - 1);
    maxX = clamp(Math.ceil(maxX + pad), minX + 1, width);
    maxY = clamp(Math.ceil(maxY + pad), minY + 1, height);

    const cropWidth = Math.max(2, maxX - minX);
    const cropHeight = Math.max(2, maxY - minY);
    const cropCanvas = document.createElement("canvas");
    cropCanvas.width = cropWidth;
    cropCanvas.height = cropHeight;
    const cropCtx = cropCanvas.getContext("2d");
    if (!cropCtx) {
      return {
        highlightedSnapshot: highlightCanvas.toDataURL("image/jpeg", 0.92),
        croppedSnapshot: baseImg?.src || "",
      };
    }

    cropCtx.fillStyle = "#ffffff";
    cropCtx.fillRect(0, 0, cropWidth, cropHeight);

    cropCtx.save();
    tracePolygonPath(cropCtx, projectedPoints, minX, minY);
    cropCtx.clip();
    cropCtx.drawImage(baseImg, -minX, -minY, width, height);
    cropCtx.restore();

    projectedDormers.forEach((dormerPoints) => {
      cropCtx.save();
      tracePolygonPath(cropCtx, dormerPoints, minX, minY);
      cropCtx.clip();
      cropCtx.fillStyle = "rgba(255, 255, 255, 0.85)";
      cropCtx.fillRect(0, 0, cropWidth, cropHeight);
      cropCtx.restore();
    });

    return {
      highlightedSnapshot: highlightCanvas.toDataURL("image/jpeg", 0.92),
      croppedSnapshot: cropCanvas.toDataURL("image/jpeg", 0.92),
    };
  };

  const buildBackgroundSegmentSnapshots = async () => {
    const baseSnapshot =
      data.segmentBaseSnapshot || data.initialSnapshot || data.segmentIrradianceSnapshot || "";
    const irradianceSnapshot =
      data.segmentIrradianceSnapshot || data.segmentBaseSnapshot || data.initialSnapshot || "";

    const [baseImg, irradianceImgRaw] = await Promise.all([
      loadDataImage(baseSnapshot),
      loadDataImage(irradianceSnapshot),
    ]);

    if (!baseImg) return {};
    const irradianceImg = irradianceImgRaw || baseImg;

    const entries = await Promise.all(
      segmentMetaForViewer.map(async (meta) => {
        const pair = buildSegmentImagePair(
          baseImg,
          irradianceImg,
          meta.boundaryPoints || [],
          meta.dormerBoundaries || []
        );
        return [
          String(meta.id),
          {
            ...pair,
            name: meta.name || `Section ${meta.id}`,
            boundaries: Array.isArray(meta.boundaryPoints) ? meta.boundaryPoints : [],
          },
        ];
      })
    );

    return Object.fromEntries(entries);
  };

  if (reportPayload?.shouldDownload === false) {
    const snapshotDataUrl = resolvePrimarySnapshot(
      data.initialSnapshot,
      data.segmentBaseSnapshot,
      data.segmentIrradianceSnapshot
    );
    const segmentSnapshots = await buildBackgroundSegmentSnapshots();
    await generateShadePdf({
      snapshotDataUrl,
      segmentSnapshots,
      shouldDownload: false,
    });
    return;
  }

  const inlineSegmentMetaJson = JSON.stringify(segmentMetaForViewer).replace(/</g, "\\u003c");

  // ---------- HTML (no scene payload embedded) ----------
  page.document.write(`
    <html>
      <head>
        <title>${data.title}</title>
        <meta charset="utf-8" />
        <style>
          @font-face {
            font-family: "Reddit Sans";
            src: url("${redditSansRegular}") format("truetype");
            font-weight: 100 900;
            font-style: normal;
            font-display: swap;
          }
          @font-face {
            font-family: "Reddit Sans";
            src: url("${redditSansItalic}") format("truetype");
            font-weight: 100 900;
            font-style: italic;
            font-display: swap;
          }
          :root {
            --shade-ink: rgba(4, 34, 62, 1);
            --shade-muted: rgba(163, 163, 163, 1);
            --shade-black: rgba(0, 0, 0, 1);
            --shade-border: 0.6px solid rgba(4, 34, 62, 1);
            --shade-inner-border: 0.6px solid rgba(4, 34, 62, 0.5);
            --shade-radius: 20px;
          }
          * {
            box-sizing: border-box;
          }
          body {
            margin: 0;
            background: #fff;
            color: var(--shade-ink);
            font-family: "Reddit Sans", "Inter", Arial, sans-serif;
          }
          .report-shell {
            width: min(1600px, calc(100vw - 24px));
            margin: 0 auto;
            padding: 28px 0 40px;
          }
          .report-top-row {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 12px;
          }
          .report-title {
            margin: 0;
            font-size: 20px;
            font-weight: 500;
            color: var(--shade-ink);
          }
          #download-pdf-btn {
            height: 44px;
            padding: 0 16px;
            border-radius: 12px;
            border: var(--shade-border);
            background: #fff;
            color: var(--shade-ink);
            font-family: "Reddit Sans", "Inter", Arial, sans-serif;
            font-size: 14px;
            font-weight: 500;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            cursor: pointer;
            transition: background 0.2s ease, opacity 0.2s ease;
          }
          #download-pdf-btn:hover {
            background: rgba(4, 34, 62, 0.06);
          }
          #download-pdf-btn:disabled {
            cursor: wait;
            opacity: 0.8;
          }
          #download-pdf-btn.is-loading:hover {
            background: #fff;
          }
          .download-btn-icon {
            width: 18px;
            height: 18px;
            display: block;
            background-color: currentColor;
            -webkit-mask: url("${generateReportIcon}") no-repeat center / contain;
            mask: url("${generateReportIcon}") no-repeat center / contain;
          }
          .download-btn-spinner {
            width: 16px;
            height: 16px;
            display: none;
            border-radius: 50%;
            border: 2px solid rgba(4, 34, 62, 0.2);
            border-top-color: var(--shade-ink);
            animation: shade-report-spin 0.8s linear infinite;
          }
          #download-pdf-btn.is-loading .download-btn-icon {
            display: none;
          }
          #download-pdf-btn.is-loading .download-btn-spinner {
            display: block;
          }
          @keyframes shade-report-spin {
            from {
              transform: rotate(0deg);
            }
            to {
              transform: rotate(360deg);
            }
          }
          .project-name {
            margin: 18px 0 0;
            font-size: 32px;
            font-weight: 700;
            color: var(--shade-ink);
            line-height: 1.15;
          }
          .project-meta {
            margin-top: 8px;
            font-size: 16px;
            font-weight: 500;
            color: var(--shade-muted);
            line-height: 1.4;
            word-break: break-word;
          }
          .report-card {
            border: none;
            background: transparent;
            padding: 0;
            margin-top: 20px;
            width: 100%;
          }
          .card-title {
            margin: 0 0 14px;
            font-size: 20px;
            font-weight: 600;
            color: var(--shade-ink);
            text-align: left;
          }
          .image-wrapper {
            position: relative;
            width: 600px;
            max-width: 100%;
            margin: 0;
          }
          .main-snapshot-grid {
            display: flex;
            gap: 16px;
            justify-content: flex-start;
            flex-wrap: wrap;
          }
          .main-snapshot-grid .image-wrapper {
            width: calc((100% - 16px) / 2);
            min-width: 320px;
          }
          .main-snapshot-grid .snapshot-frame--main {
            width: 100%;
            height: auto;
            aspect-ratio: 3 / 2;
          }
          .report-card--main-snapshot .image-wrapper {
            margin: 0;
          }
          .snapshot-frame {
            position: relative;
            width: 600px;
            height: 400px;
            max-width: 100%;
            background: #fff;
            border-radius: 20px;
            overflow: hidden;
            border: var(--shade-inner-border);
          }
          .snapshot-frame--segment {
            width: 450px;
            height: 300px;
          }
          #irradiance-image {
            position: absolute;
            inset: 0;
            width: 100%;
            height: 100%;
            object-fit: cover;
            display: block;
          }
          #maptile-image {
            position: absolute;
            inset: 0;
            width: 100%;
            height: 100%;
            object-fit: cover;
            display: block;
          }
          .stats-grid {
            display: flex;
            flex-wrap: nowrap;
            gap: 8px;
            overflow-x: auto;
          }
          .metric-card {
            border-radius: 20px;
            border: var(--shade-inner-border);
            padding: 14px 16px;
            width: 315px;
            min-width: 315px;
            height: 116px;
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            text-align: left;
            background: #fff;
          }
          .metric-label {
            font-size: 14px;
            font-weight: 500;
            color: var(--shade-muted);
            line-height: 1.2;
          }
          .metric-value {
            margin-top: 8px;
            font-size: 32px;
            font-weight: 700;
            color: var(--shade-black);
            line-height: 1.15;
          }
          .metric-note {
            margin-top: 10px;
            font-size: 12px;
            font-weight: 500;
            color: var(--shade-muted);
            line-height: 1.25;
          }
          .monthly-grid {
            display: flex;
            flex-wrap: nowrap;
            gap: 8px;
            overflow-x: auto;
          }
          .month-card {
            border-radius: 20px;
            border: var(--shade-inner-border);
            width: 100px;
            min-width: 100px;
            height: 80px;
            padding: 8px 6px;
            text-align: center;
            display: flex;
            flex-direction: column;
            justify-content: center;
            gap: 4px;
            background: #fff;
          }
          .month-label {
            font-size: 14px;
            font-weight: 500;
            color: var(--shade-muted);
          }
          .month-value {
            font-size: 26px;
            font-weight: 700;
            color: var(--shade-black);
            line-height: 1;
          }
          .table-card {
            background: transparent;
          }
          .table-inner-card {
            border: none;
            background: transparent;
            padding: 0;
          }
          .table-section-title {
            margin: 0 0 12px;
            font-size: 20px;
            font-weight: 600;
            color: rgba(0, 0, 0, 1);
          }
          .report-table {
            width: 100%;
            border-collapse: separate;
            border-spacing: 0;
          }
          .report-table th,
          .report-table td {
            padding: 0 12px;
            text-align: left;
            white-space: nowrap;
            vertical-align: middle;
          }
          .report-table th {
            background: rgba(4, 34, 62, 1);
            color: rgba(255, 255, 255, 1);
            font-size: 16px;
            font-weight: 500;
            background-clip: padding-box;
          }
          .report-table thead tr {
            height: 50px;
          }
          .report-table td {
            font-size: 18px;
            font-weight: 500;
            color: rgba(0, 0, 0, 1);
          }
          .report-table tbody tr {
            height: 40px;
          }
          .report-table tbody tr:nth-child(odd) {
            background: #fff;
          }
          .report-table tbody tr:nth-child(even) {
            background: rgba(242, 244, 245, 1);
          }
          .table-scroll {
            border-radius: 20px;
            border: none;
            background: #fff;
            overflow: hidden;
            box-shadow: 0 0 0 0.6px rgba(242, 244, 245, 1);
          }
          .table-scroll-inner {
            width: 100%;
            overflow-x: auto;
          }
          .segment-section-title {
            margin: 24px 0 0;
            font-size: 20px;
            font-weight: 600;
            color: var(--shade-ink);
          }
          .segment-card {
            display: grid;
            grid-template-columns: minmax(0, 672px) minmax(0, 340px) minmax(0, 500px);
            align-items: start;
            gap: 20px;
            page-break-inside: avoid;
            border: none;
            box-shadow: inset 0 0 0 0.6px rgba(4, 34, 62, 0.5);
            border-radius: 20px;
            padding: 20px;
            background: rgba(248, 250, 252, 1);
          }
          .segment-snapshot {
            width: 672px;
            max-width: 100%;
            min-width: 0;
          }
          .segment-snapshot-pair {
            display: flex;
            gap: 12px;
            align-items: flex-start;
          }
          .segment-snapshot-cell {
            width: 330px;
            flex: 0 0 330px;
            min-width: 0;
            position: relative;
          }
          .segment-snapshot .snapshot-frame {
            margin: 0;
          }
          .segment-snapshot .snapshot-frame--segment {
            width: 330px;
            height: 250px;
            aspect-ratio: auto;
          }
          .segment-snapshot-img {
            position: absolute;
            inset: 0;
            width: 100%;
            height: 100%;
            object-fit: contain;
            object-position: center;
            display: block;
          }
          .segment-snapshot-caption {
            margin-top: 8px;
            font-size: 12px;
            font-weight: 600;
            color: rgba(4, 34, 62, 0.75);
            text-align: center;
          }
          .segment-details {
            width: 340px;
            min-width: 340px;
            height: 250px;
            display: flex;
            flex-direction: column;
            min-width: 0;
          }
          .segment-title {
            margin: 0;
            font-size: 24px;
            font-weight: 600;
            color: rgba(4, 34, 62, 1);
          }
          .segment-metrics-list {
            margin-top: 0;
            width: 340px;
            display: flex;
            flex-direction: column;
            gap: 0;
            flex: 1;
            justify-content: center;
          }
          .segment-metric-line {
            width: 340px;
            height: 39px;
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 0;
          }
          .segment-metric-divider {
            width: 340px;
            height: 20px;
            display: flex;
            align-items: center;
          }
          .segment-metric-divider::before {
            content: "";
            width: 100%;
            border-top: 0.6px solid rgba(0, 0, 0, 0.2);
          }
          .segment-metric-copy {
            min-width: 0;
          }
          .segment-metric-heading {
            font-size: 16px;
            font-weight: 600;
            color: rgba(4, 34, 62, 1);
            line-height: 1;
          }
          .segment-metric-desc {
            margin-top: 4px;
            font-size: 14px;
            font-weight: 500;
            color: rgba(163, 163, 163, 1);
            line-height: 1;
          }
          .segment-metric-line-value {
            margin-left: 12px;
            font-size: 24px;
            font-weight: 700;
            color: rgba(4, 34, 62, 1);
            line-height: 1;
            white-space: nowrap;
          }
          .segment-months-panel {
            width: 500px;
            height: 250px;
            display: flex;
            flex-direction: column;
            justify-content: flex-end;
            gap: 16px;
            min-width: 0;
          }
          .segment-bars-row {
            width: 500px;
            height: 170px;
            display: flex;
            justify-content: space-between;
            align-items: flex-end;
            flex-wrap: nowrap;
          }
          .segment-bar-wrap {
            width: 40px;
            height: 170px;
            display: flex;
            align-items: flex-end;
            justify-content: center;
            flex: 0 0 40px;
          }
          .segment-bar {
            width: 40px;
            border-radius: 10px;
            background: rgba(4, 34, 62, 1);
          }
          .segment-months-grid {
            width: 500px;
            display: flex;
            flex-wrap: nowrap;
            justify-content: space-between;
          }
          .segment-month-card {
            width: 40px;
            height: 44px;
            border-radius: 12px;
            background: #fff;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            flex: 0 0 40px;
          }
          .segment-month-label {
            font-size: 12px;
            font-weight: 500;
            color: rgba(163, 163, 163, 1);
            line-height: 1;
          }
          .segment-month-value {
            margin-top: 4px;
            font-size: 13px;
            font-weight: 700;
            color: rgba(0, 0, 0, 1);
            line-height: 1;
          }
          .edit-view-btn {
            position: absolute;
            top: 12px;
            right: 12px;
            width: 40px;
            height: 40px;
            padding: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 10px;
            border: none;
            background: rgba(0, 0, 0, 0.6);
            color: rgba(255, 255, 255, 1);
            cursor: pointer;
          }
          .edit-view-btn:disabled {
            opacity: 0.6;
            cursor: default;
          }
          .edit-view-btn.loading::after {
            content: "";
            width: 14px;
            height: 14px;
            border: 2px solid rgba(4, 34, 62, 0.25);
            border-top-color: rgba(4, 34, 62, 0.95);
            border-radius: 50%;
            animation: spin 0.8s linear infinite;
            margin-left: 6px;
          }
          .edit-icon {
            width: 24px;
            height: 24px;
            fill: currentColor;
          }
          @keyframes spin { to { transform: rotate(360deg); } }

          #viewer-overlay {
            position: fixed;
            inset: 0;
            background: rgba(0,0,0,0.5);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 9999;
            opacity: 0;
            pointer-events: none;
            transition: opacity 0.25s ease-out;
          }
          #viewer-overlay.show { opacity: 1; pointer-events: auto; }

          #viewer-container {
            width: 800px;
            max-width: 100%;
            height: 450px;
            background: #111;
            border-radius: 10px;
            box-shadow: 0 18px 45px rgba(0,0,0,0.45);
            border: 1px solid rgba(255,255,255,0.12);
            position: relative;
            overflow: hidden;
          }

          #viewer-overlay.show #viewer-container { animation: slideUpFade 0.3s ease-out; }

          @keyframes slideUpFade {
            from { transform: translateY(40px); opacity: 0; }
            to { transform: translateY(0); opacity: 1; }
          }

          #viewer-canvas { width: 100%; height: 100%; display: block; }
          #viewer-controls { position: absolute; bottom: 12px; right: 12px; display:flex; gap: 8px; }

          .viewer-btn {
            padding: 8px 14px;
            font-size: 13px;
            border-radius: 4px;
            border: 1px solid #d0d0d0;
            background: #1890ff;
            color: #fff;
            cursor: pointer;
            min-width: 80px;
          }
          .viewer-btn:hover { background: #1677c3; }
          .viewer-btn:disabled { opacity: 0.6; cursor: default; }

          #sun-toggle-btn {
            position: absolute;
            top: 12px;
            right: 12px;
            width: 36px;
            height: 36px;
            padding: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 50%;
            border: 1px solid #f0c14b;
            background: #ffe58f;
            color: #b37f00;
            cursor: pointer;
            z-index: 2;
          }
          #sun-toggle-btn .sun-icon { width: 18px; height: 18px; fill: currentColor; }
          #sun-toggle-btn.off { background: #f0f0f0; border-color: #d9d9d9; color: #999; }

          #viewer-loading {
            position: absolute;
            inset: 0;
            display: none;
            align-items: center;
            justify-content: center;
            color: #fff;
            font-family: "Reddit Sans", "Inter", Arial, sans-serif;
            font-size: 14px;
            background: rgba(0,0,0,0.35);
            z-index: 3;
          }
          #viewer-loading.show { display: flex; }
          .spinner {
            width: 18px;
            height: 18px;
            border: 2px solid rgba(255,255,255,0.4);
            border-top-color: rgba(255,255,255,0.95);
            border-radius: 50%;
            animation: spin2 0.8s linear infinite;
            margin-right: 10px;
          }
          @keyframes spin2 { to { transform: rotate(360deg); } }

          @media (max-width: 1100px) {
            .segment-card {
              grid-template-columns: 1fr;
            }
            .segment-snapshot {
              width: 100%;
            }
            .snapshot-frame--segment {
              width: 100%;
              height: auto;
              aspect-ratio: 3 / 2;
            }
            .segment-details,
            .segment-metrics-list,
            .segment-metric-line {
              width: 100%;
              min-width: 0;
            }
            .segment-metric-divider {
              width: 100%;
            }
            .segment-details {
              height: auto;
              display: block;
            }
            .segment-metrics-list {
              margin-top: 14px;
              flex: none;
              justify-content: flex-start;
            }
            .segment-months-panel {
              width: 100%;
              height: auto;
              overflow-x: auto;
            }
            .segment-bars-row,
            .segment-months-grid {
              width: 500px;
            }
          }
          @media (max-width: 760px) {
            .report-shell {
              width: calc(100vw - 24px);
              padding: 20px 0 30px;
            }
            .report-top-row {
              flex-direction: column;
              align-items: flex-start;
            }
            #download-pdf-btn {
              width: 100%;
            }
            .project-name {
              font-size: 28px;
            }
            .image-wrapper,
            .snapshot-frame {
              width: 100%;
            }
            .main-snapshot-grid {
              gap: 12px;
            }
            .main-snapshot-grid .image-wrapper {
              width: 100%;
              min-width: 0;
            }
            .snapshot-frame {
              height: auto;
              aspect-ratio: 3 / 2;
            }
            .metric-value {
              font-size: 28px;
            }
          }
        </style>
      </head>
      <body>
        <div class="report-shell">
          <div class="report-top-row">
            <h1 class="report-title">Shade Report</h1>
            <button id="download-pdf-btn" type="button">
              <span class="download-btn-icon" aria-hidden="true"></span>
              <span class="download-btn-spinner" aria-hidden="true"></span>
              <span class="download-btn-label">Download PDF</span>
            </button>
          </div>

          <div class="project-name">${currentDesign?.name || "Untitled Design"}</div>
          <div class="project-meta">${data.address} | ${data.dateTime}</div>

          <div class="report-card report-card--main-snapshot">
            <div class="main-snapshot-grid">
              <div class="image-wrapper">
                <div class="snapshot-frame snapshot-frame--main">
                  <img id="irradiance-image" src="${data.initialSnapshot || ""}" alt="Annual Irradiance Snapshot" />
                </div>
                <button id="edit-view-btn" class="edit-view-btn" title="Edit View" aria-label="Edit View">
                  <svg class="edit-icon" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"></path>
                  </svg>
                </button>
              </div>
              <div class="image-wrapper">
                <div class="snapshot-frame snapshot-frame--main">
                  <img id="maptile-image" src="${data.mapTileImage || ""}" alt="Map Tile Snapshot" />
                </div>
              </div>
            </div>
          </div>

          <div class="report-card table-card">
            <h2 class="table-section-title">Section Summary</h2>
            <div class="table-scroll">
              <div class="table-scroll-inner">
                <table class="report-table">
                  <thead>
                    <tr>
                      <th>Sections</th>
                      <th>Azimuth</th>
                      <th>Pitch</th>
                      <th>TOF (%)</th>
                      <th>Annual Solar Access (%)</th>
                      <th>Annual TSRF (%)</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${data.summary.map((r, i) => `
                      <tr>
                        <td>${toSegmentCode(i)}</td>
                        <td>${r.azimuth}</td>
                        <td>${r.pitch}</td>
                        <td>${r.tofDisplay}</td>
                        <td>${r.solarAccessDisplay}</td>
                        <td>${r.tsrfDisplay}</td>
                      </tr>
                    `).join("")}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div class="report-card table-card">
            <h2 class="card-title">Monthly Solar Access % by Section</h2>
            <div class="table-inner-card table-scroll">
              <div class="table-scroll-inner">
                <table class="report-table">
                  <thead>
                    <tr>
                      <th>Sections</th>
                      ${months.map((m) => `<th>${m}</th>`).join("")}
                    </tr>
                  </thead>
                  <tbody>
                    ${data.monthlySolarAccess.map((row, i) => `
                      <tr>
                        <td>${toSegmentCode(i)}</td>
                        ${(row || []).map((val) => `<td>${val}</td>`).join("")}
                      </tr>
                    `).join("")}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <h2 class="segment-section-title">Section Details</h2>
          ${(data.segments || []).map((seg) => `
            <div class="report-card segment-card">
              <div class="segment-snapshot">
                <div class="segment-snapshot-pair" data-seg-id="${seg.id}">
                  <div class="segment-snapshot-cell segment-snapshot-cell--highlight">
                    <div class="snapshot-frame snapshot-frame--segment">
                      <img
                        class="segment-snapshot-img segment-snapshot-img--highlight"
                        data-seg-id="${seg.id}"
                        src="${data.segmentIrradianceSnapshot || data.segmentBaseSnapshot || data.initialSnapshot || ""}"
                        alt="Section Irradiance Snapshot ${seg.label}"
                      />
                    </div>
                    <div class="segment-snapshot-caption">Section Irradiance</div>
                  </div>
                  <div class="segment-snapshot-cell">
                    <div class="snapshot-frame snapshot-frame--segment">
                      <img
                        class="segment-snapshot-img segment-snapshot-img--crop"
                        data-seg-id="${seg.id}"
                        src="${data.segmentBaseSnapshot || data.initialSnapshot || ""}"
                        alt="Section Crop Snapshot ${seg.label}"
                      />
                    </div>
                    <div class="segment-snapshot-caption">Section Crop</div>
                  </div>
                </div>
              </div>
              <div class="segment-details">
                <h3 class="segment-title">Section ${seg.label}</h3>
                <div class="segment-metrics-list">
                  <div class="segment-metric-line">
                    <div class="segment-metric-copy">
                      <div class="segment-metric-heading">TOF</div>
                      <div class="segment-metric-desc">Total Obstruction Factor</div>
                    </div>
                    <div class="segment-metric-line-value">${seg.tofDisplay}</div>
                  </div>
                  <div class="segment-metric-divider"></div>
                  <div class="segment-metric-line">
                    <div class="segment-metric-copy">
                      <div class="segment-metric-heading">ASA</div>
                      <div class="segment-metric-desc">Annual Solar Access</div>
                    </div>
                    <div class="segment-metric-line-value">${seg.solarAccessDisplay}</div>
                  </div>
                  <div class="segment-metric-divider"></div>
                  <div class="segment-metric-line">
                    <div class="segment-metric-copy">
                      <div class="segment-metric-heading">TSRF</div>
                      <div class="segment-metric-desc">Total Solar Resource Fraction</div>
                    </div>
                    <div class="segment-metric-line-value">${seg.tsrfDisplay}</div>
                  </div>
                </div>
              </div>
              <div class="segment-months-panel">
                <div class="segment-bars-row">
                  ${months.map((m, i) => `
                    <div class="segment-bar-wrap">
                      <div
                        class="segment-bar"
                        style="height:${Math.max(2, Math.round(((Number(seg.monthlyASA?.[i] ?? 0) || 0) / 100) * 170))}px;"
                      ></div>
                    </div>
                  `).join("")}
                </div>
                <div class="segment-months-grid">
                  ${months.map((m, i) => `
                    <div class="segment-month-card">
                      <div class="segment-month-label">${m}</div>
                      <div class="segment-month-value">${seg.monthlyASA?.[i] ?? 0}%</div>
                    </div>
                  `).join("")}
                </div>
              </div>
            </div>
          `).join("")}
        </div>
        <div id="viewer-overlay">
          <div id="viewer-container">
            <div id="viewer-loading">
              <div class="spinner"></div>
              Loading 3D view...
            </div>

            <button id="sun-toggle-btn" title="Toggle Irradiance" aria-label="Toggle Irradiance" disabled>
              <svg class="sun-icon" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8z"></path>
              </svg>
            </button>

            <canvas id="viewer-canvas"></canvas>
            <div id="viewer-controls">
              <button id="viewer-done-btn" class="viewer-btn" disabled>Done</button>
              <button id="viewer-cancel-btn" class="viewer-btn">Cancel</button>
            </div>
          </div>
        </div>

        <script>
          (function () {
            var openerWin = window.opener;

            // ✅ Tell opener we're ready immediately
            if (openerWin && openerWin.postMessage) {
              openerWin.postMessage({ type: "SHADE_REPORT_READY" }, "*");
            }

            var imgEl = document.getElementById("irradiance-image");
            var editBtn = document.getElementById("edit-view-btn");
            var segmentHighlightImgs = Array.prototype.slice.call(
              document.querySelectorAll(".segment-snapshot-img--highlight")
            );

            var overlay = document.getElementById("viewer-overlay");
            var canvas = document.getElementById("viewer-canvas");
            var loadingEl = document.getElementById("viewer-loading");
            var sunBtn = document.getElementById("sun-toggle-btn");
            var doneBtn = document.getElementById("viewer-done-btn");
            var cancelBtn = document.getElementById("viewer-cancel-btn");

            var THREE = openerWin && openerWin.THREE;
            var OrbitControlsCtor = openerWin && openerWin.OrbitControls;
            var segmentMetaList = ${inlineSegmentMetaJson};
            var segmentClearBaseSnapshot = ${JSON.stringify(
              data.segmentBaseSnapshot || data.initialSnapshot || ""
            ).replace(/</g, "\\u003c")};
            var segmentIrradianceBaseSnapshot = ${JSON.stringify(
              data.segmentIrradianceSnapshot ||
                data.segmentBaseSnapshot ||
                data.initialSnapshot ||
                ""
            ).replace(/</g, "\\u003c")};
            var reportCamera = ${JSON.stringify(
              reportPayload?.segmentBaseCamera || reportPayload?.camera || {}
            ).replace(/</g, "\\u003c")};

            var renderer = null;
            var scene = null;
            var camera = null;
            var controls = null;

            var sceneLoaded = false;
            var sceneRequested = false;

            // ✅ NEW: if user clicks Edit View while still loading, auto-open overlay when ready
            var openOverlayWhenReady = false;
            var segmentBaseSnapshots = {};
            var segmentRenderedSnapshots = {};
            var segmentRenderPromises = {};
            var imageLoadCache = {};
            var segmentMetaById = {};

            var IRRADIANCE_SPRITE_NAME = "IRRADIANCE_SPRITE";
            var sunOn = true;

            function showViewerLoading(on) {
              if (!loadingEl) return;
              loadingEl.classList.toggle("show", !!on);
            }

            function getHighlightImg(segId) {
              return document.querySelector('.segment-snapshot-img--highlight[data-seg-id="' + segId + '"]');
            }

            function getCropImg(segId) {
              return document.querySelector('.segment-snapshot-img--crop[data-seg-id="' + segId + '"]');
            }

            function getAllSegmentIds() {
              return segmentHighlightImgs
                .map(function (img) { return img.getAttribute("data-seg-id"); })
                .filter(function (segId) { return !!segId; });
            }

            function loadImage(src) {
              if (!src || typeof src !== "string") return Promise.resolve(null);
              if (!imageLoadCache[src]) {
                imageLoadCache[src] = new Promise(function (resolve) {
                  var img = new Image();
                  img.onload = function () { resolve(img); };
                  img.onerror = function () { resolve(null); };
                  img.src = src;
                });
              }
              return imageLoadCache[src];
            }

            function clamp(value, min, max) {
              return Math.min(max, Math.max(min, value));
            }

            function getProjectionCamera(width, height) {
              if (!THREE || !width || !height) return null;
              var fov = (reportCamera && reportCamera.fov) || 45;
              var cam = new THREE.PerspectiveCamera(fov, width / height, 0.1, 5000);
              var camUp = (reportCamera && reportCamera.up) || [0, 0, 1];
              cam.up.set(camUp[0] || 0, camUp[1] || 0, camUp[2] || 1);

              var camPos = (reportCamera && reportCamera.position) || [0, 0, 50];
              var camTarget = (reportCamera && reportCamera.target) || [0, 0, 0];
              cam.position.set(camPos[0] || 0, camPos[1] || 0, camPos[2] || 50);
              cam.lookAt(new THREE.Vector3(camTarget[0] || 0, camTarget[1] || 0, camTarget[2] || 0));
              cam.updateMatrixWorld(true);
              cam.updateProjectionMatrix();
              return cam;
            }

            function projectBoundaryPoints(boundaryPoints, width, height) {
              if (!THREE || !Array.isArray(boundaryPoints) || boundaryPoints.length < 3) return [];
              var projectionCamera = getProjectionCamera(width, height);
              if (!projectionCamera) return [];

              return boundaryPoints.map(function (point) {
                var vec = new THREE.Vector3(point.x || 0, point.y || 0, point.z || 0);
                vec.project(projectionCamera);
                return {
                  x: ((vec.x + 1) * 0.5) * width,
                  y: ((1 - vec.y) * 0.5) * height,
                };
              });
            }

            function tracePolygonPath(ctx, points, shiftX, shiftY) {
              if (!ctx || !Array.isArray(points) || points.length < 3) return;
              ctx.beginPath();
              points.forEach(function (point, index) {
                var x = point.x - (shiftX || 0);
                var y = point.y - (shiftY || 0);
                if (index === 0) ctx.moveTo(x, y);
                else ctx.lineTo(x, y);
              });
              ctx.closePath();
            }

            function buildSegmentImagePair(baseImg, irradianceImg, boundaryPoints, dormerBoundaries) {
              var sourceWidth = baseImg.naturalWidth || baseImg.width || 1;
              var sourceHeight = baseImg.naturalHeight || baseImg.height || 1;
              var maxDimension = 1400;
              var downscale = Math.max(1, Math.max(sourceWidth, sourceHeight) / maxDimension);
              var width = Math.max(1, Math.round(sourceWidth / downscale));
              var height = Math.max(1, Math.round(sourceHeight / downscale));

              var projectedPoints = projectBoundaryPoints(boundaryPoints, width, height);
              if (projectedPoints.length < 3) {
                return {
                  highlightedSnapshot: baseImg.src,
                  croppedSnapshot: baseImg.src,
                };
              }

              // Project each contained dormer outline so it can be masked out of
              // the parent segment's crop/irradiance — a dormer is a separate
              // segment and must not read as part of this one.
              var projectedDormers = (Array.isArray(dormerBoundaries) ? dormerBoundaries : [])
                .map(function (dormer) {
                  return projectBoundaryPoints(dormer, width, height);
                })
                .filter(function (pts) {
                  return Array.isArray(pts) && pts.length >= 3;
                });

              var highlightCanvas = document.createElement("canvas");
              highlightCanvas.width = width;
              highlightCanvas.height = height;
              var highlightCtx = highlightCanvas.getContext("2d");
              if (!highlightCtx) {
                return {
                  highlightedSnapshot: baseImg.src,
                  croppedSnapshot: baseImg.src,
                };
              }
              highlightCtx.drawImage(baseImg, 0, 0, width, height);

              if (irradianceImg) {
                highlightCtx.save();
                tracePolygonPath(highlightCtx, projectedPoints, 0, 0);
                highlightCtx.clip();
                highlightCtx.drawImage(irradianceImg, 0, 0, width, height);
                highlightCtx.restore();
              }

              // Mask dormers out of the irradiance overlay: restore the plain
              // roof there and veil it so it clearly isn't part of this segment.
              projectedDormers.forEach(function (dormerPts) {
                highlightCtx.save();
                tracePolygonPath(highlightCtx, dormerPts, 0, 0);
                highlightCtx.clip();
                highlightCtx.drawImage(baseImg, 0, 0, width, height);
                highlightCtx.fillStyle = "rgba(255, 255, 255, 0.5)";
                highlightCtx.fillRect(0, 0, width, height);
                highlightCtx.restore();
              });

              var padded = projectedPoints.map(function (point) {
                return {
                  x: clamp(point.x, 0, width),
                  y: clamp(point.y, 0, height),
                };
              });
              var minX = Math.min.apply(null, padded.map(function (p) { return p.x; }));
              var minY = Math.min.apply(null, padded.map(function (p) { return p.y; }));
              var maxX = Math.max.apply(null, padded.map(function (p) { return p.x; }));
              var maxY = Math.max.apply(null, padded.map(function (p) { return p.y; }));
              if (!isFinite(minX) || !isFinite(minY) || !isFinite(maxX) || !isFinite(maxY)) {
                return {
                  highlightedSnapshot: highlightCanvas.toDataURL("image/jpeg", 0.92),
                  croppedSnapshot: baseImg.src,
                };
              }
              var pad = Math.max(18, Math.round(Math.min(width, height) * 0.08));

              minX = clamp(Math.floor(minX - pad), 0, width - 1);
              minY = clamp(Math.floor(minY - pad), 0, height - 1);
              maxX = clamp(Math.ceil(maxX + pad), minX + 1, width);
              maxY = clamp(Math.ceil(maxY + pad), minY + 1, height);

              var cropWidth = Math.max(2, maxX - minX);
              var cropHeight = Math.max(2, maxY - minY);

              var cropCanvas = document.createElement("canvas");
              cropCanvas.width = cropWidth;
              cropCanvas.height = cropHeight;
              var cropCtx = cropCanvas.getContext("2d");
              if (!cropCtx) {
                return {
                  highlightedSnapshot: highlightCanvas.toDataURL("image/jpeg", 0.92),
                  croppedSnapshot: baseImg.src,
                };
              }
              cropCtx.fillStyle = "#ffffff";
              cropCtx.fillRect(0, 0, cropWidth, cropHeight);

              cropCtx.save();
              tracePolygonPath(cropCtx, projectedPoints, minX, minY);
              cropCtx.clip();
              cropCtx.drawImage(baseImg, -minX, -minY, width, height);
              cropCtx.restore();

              // White-out the dormer footprint so the crop shows only this
              // segment's roof, not the dormer sitting inside it.
              projectedDormers.forEach(function (dormerPts) {
                cropCtx.save();
                tracePolygonPath(cropCtx, dormerPts, minX, minY);
                cropCtx.clip();
                cropCtx.fillStyle = "rgba(255, 255, 255, 0.85)";
                cropCtx.fillRect(0, 0, cropWidth, cropHeight);
                cropCtx.restore();
              });

              return {
                highlightedSnapshot: highlightCanvas.toDataURL("image/jpeg", 0.92),
                croppedSnapshot: cropCanvas.toDataURL("image/jpeg", 0.92),
              };
            }

            function renderSegmentSnapshotPair(segId) {
              var segMeta = segmentMetaById[segId] || {};
              var baseSnapshot = segmentBaseSnapshots[segId];
              if (!baseSnapshot || typeof baseSnapshot !== "string" || !baseSnapshot.startsWith("data:image/")) {
                return Promise.resolve();
              }

              var irradianceSnapshot =
                typeof segmentIrradianceBaseSnapshot === "string" &&
                segmentIrradianceBaseSnapshot.startsWith("data:image/")
                  ? segmentIrradianceBaseSnapshot
                  : baseSnapshot;

              var promise = Promise.all([
                loadImage(baseSnapshot),
                loadImage(irradianceSnapshot),
              ]).then(function (images) {
                var baseImg = images[0];
                var irradianceImg = images[1] || images[0];
                if (!baseImg) return;
                var pair = buildSegmentImagePair(
                  baseImg,
                  irradianceImg,
                  segMeta.boundaryPoints || [],
                  segMeta.dormerBoundaries || []
                );
                var highlightImg = getHighlightImg(segId);
                var cropImg = getCropImg(segId);
                if (highlightImg && pair.highlightedSnapshot) {
                  highlightImg.src = pair.highlightedSnapshot;
                }
                if (cropImg && pair.croppedSnapshot) {
                  cropImg.src = pair.croppedSnapshot;
                }
                segmentRenderedSnapshots[segId] = {
                  highlightedSnapshot: pair.highlightedSnapshot,
                  croppedSnapshot: pair.croppedSnapshot,
                  name: segMeta.name || ("Section " + segId),
                  boundaries: Array.isArray(segMeta.boundaryPoints) ? segMeta.boundaryPoints : [],
                };
              });

              segmentRenderPromises[segId] = promise;
              return promise;
            }

            function rerenderAllSegments() {
              getAllSegmentIds().forEach(function (segId) {
                renderSegmentSnapshotPair(segId);
              });
            }

            function applyMainSnapshot(snap) {
              if (!snap || typeof snap !== "string" || !snap.startsWith("data:image/")) return;
              if (imgEl) imgEl.src = snap;
            }

            function collectSegmentSnapshots() {
              var map = {};
              getAllSegmentIds().forEach(function (segId) {
                var rendered = segmentRenderedSnapshots[segId] || {};
                var highlightImg = getHighlightImg(segId);
                var cropImg = getCropImg(segId);
                var highlightedSnapshot =
                  rendered.highlightedSnapshot ||
                  (highlightImg && highlightImg.src && highlightImg.src.startsWith("data:image/")
                    ? highlightImg.src
                    : "");
                var croppedSnapshot =
                  rendered.croppedSnapshot ||
                  (cropImg && cropImg.src && cropImg.src.startsWith("data:image/")
                    ? cropImg.src
                    : "");
                var segMeta = segmentMetaById[segId] || {};

                if (!highlightedSnapshot && !croppedSnapshot) return;
                map[segId] = {
                  highlightedSnapshot: highlightedSnapshot,
                  croppedSnapshot: croppedSnapshot,
                  name: segMeta.name || ("Section " + segId),
                  boundaries: Array.isArray(segMeta.boundaryPoints) ? segMeta.boundaryPoints : [],
                };
              });
              return map;
            }

            (segmentMetaList || []).forEach(function (meta) {
              if (!meta || !meta.id) return;
              segmentMetaById[String(meta.id)] = meta;
            });

            getAllSegmentIds().forEach(function (segId) {
              if (
                typeof segmentClearBaseSnapshot === "string" &&
                segmentClearBaseSnapshot.startsWith("data:image/")
              ) {
                segmentBaseSnapshots[segId] = segmentClearBaseSnapshot;
              } else {
                var cropImg = getCropImg(segId);
                var highlightImg = getHighlightImg(segId);
                if (cropImg && cropImg.src && cropImg.src.startsWith("data:image/")) {
                  segmentBaseSnapshots[segId] = cropImg.src;
                } else if (
                  highlightImg &&
                  highlightImg.src &&
                  highlightImg.src.startsWith("data:image/")
                ) {
                  segmentBaseSnapshots[segId] = highlightImg.src;
                }
              }
              if (!segmentMetaById[segId]) {
                segmentMetaById[segId] = {
                  id: segId,
                  name: "Section " + segId,
                  boundaryPoints: [],
                };
              }
            });

            rerenderAllSegments();

            function setButtonsEnabled(enabled) {
              if (sunBtn) sunBtn.disabled = !enabled;
              if (doneBtn) doneBtn.disabled = !enabled;
            }

            function updateSize() {
              if (!renderer || !camera) return;
              var w = canvas.clientWidth || 800;
              var h = canvas.clientHeight || 450;
              if (w <= 0 || h <= 0) return;
              renderer.setSize(w, h, false);
              camera.aspect = w / h;
              camera.updateProjectionMatrix();
            }

            function setIrradianceVisibility(visible) {
              if (!scene) return;
              scene.traverse(function (obj) {
                if (obj && obj.name === IRRADIANCE_SPRITE_NAME) {
                  obj.visible = visible;
                }
              });
            }

            function frameViewerCameraIfNeeded(root, viewerCamera, viewerControls) {
              if (!THREE || !root || !viewerCamera || !viewerControls) return;

              var bounds = new THREE.Box3().setFromObject(root);
              if (bounds.isEmpty()) return;

              var center = bounds.getCenter(new THREE.Vector3());
              var size = bounds.getSize(new THREE.Vector3());
              var radius = Math.max(size.length() / 2, 1);
              var currentTarget = viewerControls.target
                ? viewerControls.target.clone()
                : new THREE.Vector3(0, 0, 0);
              var currentPosition = viewerCamera.position
                ? viewerCamera.position.clone()
                : new THREE.Vector3(0, 0, 50);
              var targetLooksInvalid =
                !isFinite(currentTarget.x) ||
                !isFinite(currentTarget.y) ||
                !isFinite(currentTarget.z) ||
                currentPosition.distanceTo(currentTarget) < 1e-3;
              var paddedBounds = bounds.clone().expandByScalar(Math.max(radius * 0.05, 1));
              var targetOutsideScene = !paddedBounds.containsPoint(currentTarget);

              if (!targetLooksInvalid && !targetOutsideScene) return;

              var direction = currentPosition.sub(currentTarget);
              if (direction.lengthSq() < 1e-6) {
                direction = new THREE.Vector3(0, -1, 0.65);
              }
              direction.normalize();

              var fovRadians = ((viewerCamera.fov || 45) * Math.PI) / 180;
              var fitDistance = radius / Math.tan(Math.max(fovRadians / 2, 0.01));
              var nextDistance = Math.max(fitDistance * 1.15, radius * 1.5, 10);

              viewerControls.target.copy(center);
              viewerCamera.position.copy(center.clone().add(direction.multiplyScalar(nextDistance)));
              viewerCamera.near = Math.max(0.1, nextDistance / 1000);
              viewerCamera.far = Math.max(5000, nextDistance * 20);
              viewerCamera.updateProjectionMatrix();
              viewerControls.update();
            }

            function initViewer(payload) {
              if (!canvas || !THREE || !OrbitControlsCtor) {
                console.warn("Viewer dependencies not available.");
                return;
              }

              renderer = new THREE.WebGLRenderer({ canvas: canvas, antialias: true });
              renderer.setPixelRatio(window.devicePixelRatio || 1);

              scene = new THREE.Scene();
              var loader = new THREE.ObjectLoader();

              try {
                var loadedScene = loader.parse(payload.scene);
                scene.add(loadedScene);
              } catch (e) {
                console.error("Failed to load scene from payload:", e);
                return;
              }

              var cameraFov = (payload.camera && payload.camera.fov) || 45;
              camera = new THREE.PerspectiveCamera(cameraFov, 800 / 450, 0.1, 5000);
              var camUp = (payload.camera && payload.camera.up) || [0, 0, 1];
              camera.up.set(camUp[0] || 0, camUp[1] || 0, camUp[2] || 1);

              var camPos = (payload.camera && payload.camera.position) || [0, 0, 50];
              camera.position.set(camPos[0], camPos[1], camPos[2]);

              controls = new OrbitControlsCtor(camera, renderer.domElement);
              controls.mouseButtons = {
                LEFT: null,
                MIDDLE: THREE.MOUSE.PAN,
                RIGHT: THREE.MOUSE.ROTATE,
              };

              controls.zoomToCursor = true;
              controls.rotateSpeed = 1;
              controls.enablePan = true;
              controls.maxPolarAngle = Math.PI / 2;

              var camTarget = (payload.camera && payload.camera.target) || [0, 0, 0];
              controls.target.set(camTarget[0], camTarget[1], camTarget[2]);
              camera.lookAt(controls.target);
              controls.update();
              frameViewerCameraIfNeeded(loadedScene, camera, controls);

              var ambient = new THREE.AmbientLight(0xffffff, 0.6);
              scene.add(ambient);

              var dir = new THREE.DirectionalLight(0xffffff, 0.8);
              dir.position.set(5, 10, 7);
              scene.add(dir);

              function animate() {
                requestAnimationFrame(animate);
                if (controls) controls.update();
                if (renderer && scene && camera) renderer.render(scene, camera);
              }

              updateSize();
              animate();

              setIrradianceVisibility(true);
              sunOn = true;
              if (sunBtn) sunBtn.classList.remove("off");

              sceneLoaded = true;
              showViewerLoading(false);
              setButtonsEnabled(true);

              // ✅ scene ready: re-enable edit button, remove spinner
              if (editBtn) {
                editBtn.disabled = false;
                editBtn.title = "Edit View";
                editBtn.classList.remove("loading");
              }

              // ✅ NEW: auto-open overlay if user clicked while loading
              if (openOverlayWhenReady && overlay) {
                openOverlayWhenReady = false;
                overlay.classList.add("show");
                updateSize();
              }
            }

            function captureSnapshotFromViewer() {
              if (!renderer) return "";
              updateSize();
              renderer.render(scene, camera);
              try {
                return renderer.domElement.toDataURL("image/jpeg", 1.0);
              } catch (e) {
                return "";
              }
            }

            // ✅ Edit View:
            // First click -> background load only (page remains scrollable)
            // If clicked while loading -> remember and auto-open when ready
            if (editBtn) {
              editBtn.addEventListener("click", function () {
                if (!sceneLoaded) {
                  // user intent: open overlay when ready
                  openOverlayWhenReady = true;
                  if (overlay) overlay.classList.add("show");
                  showViewerLoading(true);

                  if (!sceneRequested) {
                    sceneRequested = true;

                    // show loading state on button
                    editBtn.disabled = true;
                    editBtn.title = "Preparing 3D view...";
                    editBtn.classList.add("loading");

                    if (openerWin && openerWin.postMessage) {
                      openerWin.postMessage({ type: "REQUEST_SHADE_REPORT_SCENE" }, "*");
                    }
                  }
                  return;
                }

                if (sceneLoaded && overlay) {
                  overlay.classList.add("show");
                  updateSize();
                }
              });
            }

            if (doneBtn && overlay) {
              doneBtn.addEventListener("click", function () {
                var snap = captureSnapshotFromViewer();
                if (snap) applyMainSnapshot(snap);
                overlay.classList.remove("show");
              });
            }

            if (cancelBtn && overlay) {
              cancelBtn.addEventListener("click", function () {
                overlay.classList.remove("show");
              });
            }

            if (sunBtn) {
              sunBtn.addEventListener("click", function () {
                if (!sceneLoaded) return;
                sunOn = !sunOn;
                setIrradianceVisibility(sunOn);
                sunBtn.classList.toggle("off", !sunOn);
              });
            }

            // ✅ Receive messages:
            window.addEventListener("message", function (event) {
              if (event?.data?.type === "SHADE_REPORT_SCENE_DATA") {
                try {
                  showViewerLoading(true);
                  initViewer(event.data.payload);
                } catch (e) {
                  console.error("initViewer failed:", e);
                  showViewerLoading(false);
                  setButtonsEnabled(false);
                }
              }

              if (event?.data?.type === "SHADE_REPORT_SCENE_ERROR") {
                console.error("Scene export error:", event.data.error);
                showViewerLoading(false);
                setButtonsEnabled(false);
                if (editBtn) {
                  editBtn.disabled = false;
                  editBtn.title = "Edit View (failed)";
                  editBtn.classList.remove("loading");
                }
              }

              // ✅ Fallback snapshot updater from opener
              if (event?.data?.type === "SHADE_REPORT_SET_SNAPSHOT") {
                var s = event.data.snapshot;
                if (imgEl && s && typeof s === "string" && s.startsWith("data:image/")) {
                  applyMainSnapshot(s);
                }
              }
            });

            // ✅ Download PDF: use viewer snapshot if available else current image
            var downloadBtn = document.getElementById("download-pdf-btn");
            var downloadBtnLabel = downloadBtn
              ? downloadBtn.querySelector(".download-btn-label")
              : null;
            var defaultDownloadBtnLabel = downloadBtnLabel
              ? downloadBtnLabel.textContent
              : "Download PDF";

            function setDownloadButtonLoading(isLoading) {
              if (!downloadBtn) return;
              downloadBtn.classList.toggle("is-loading", !!isLoading);
              downloadBtn.disabled = !!isLoading;
              if (downloadBtnLabel) {
                downloadBtnLabel.textContent = isLoading
                  ? "Preparing PDF..."
                  : defaultDownloadBtnLabel;
              }
            }

            if (downloadBtn) {
              downloadBtn.addEventListener("click", function () {
                setDownloadButtonLoading(true);
                var snap = "";
                if (sceneLoaded) snap = captureSnapshotFromViewer();
                if (!snap && imgEl && imgEl.src) snap = imgEl.src;
                if (snap && imgEl) applyMainSnapshot(snap);

                var renderWaits = getAllSegmentIds().map(function (segId) {
                  return segmentRenderPromises[segId] || Promise.resolve();
                });

                Promise.all(renderWaits).then(function () {
                  if (openerWin && openerWin.postMessage) {
                    openerWin.postMessage(
                      {
                        type: "SHADE_REPORT_SNAPSHOT",
                        snapshot: snap,
                        segmentSnapshots: collectSegmentSnapshots(),
                      },
                      "*"
                    );
                  } else {
                    setDownloadButtonLoading(false);
                  }
                });

              });
            }

            window.addEventListener("message", function (event) {
              if (event?.data?.type !== "SHADE_REPORT_PDF_STATUS") return;

              var status = event.data.status;
              if (status === "loading") {
                setDownloadButtonLoading(true);
                return;
              }

              setDownloadButtonLoading(false);

              if (status === "error" && event.data.message) {
                toast.error(event.data.message, {
                  key: "shade-report-download-error",
                });
              }
            });

            window.addEventListener("resize", updateSize);
          })();
        </script>
      </body>
    </html>
  `);

  page.document.close();
  await readyPromise;
}
